/**
 * Staff Records Page
 * Refactored to use modular components for better maintainability
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PLANNER_CATEGORIES } from "@/constants/plannerCategories";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Trash2, Users, Edit, Shield, Printer, FileText, ClipboardList, Upload, Download, AlertTriangle, Mail, X, ChevronDown, ChevronUp, ScrollText, Info, Archive, RotateCcw } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format, parseISO, addMonths } from "date-fns";
import { exportStaffRecordPDF } from "@/utils/pdfExport";
import AppShell from "@/components/layout/AppShell";

// Stacked upload display — collapses 2+ files into a count badge with expand/collapse
function StackedUploads({ uploads, onDownload, onDelete, recordId, typeKey, uploadingKey, onUpload, hideUpload = false }) {
  const [expanded, setExpanded] = useState(false);
  const isUploading = uploadingKey === `${recordId}-${typeKey}`;
  const hasNew = uploads.some(u => u.viewedByDirector === false);

  return (
    <div className="flex flex-col items-center gap-1 min-w-0">
      {uploads.length === 0 ? null : uploads.length === 1 ? (
        // Single upload — show inline as before
        <span className="inline-flex items-center gap-0.5 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 text-xs">
          <button onClick={() => onDownload(uploads[0].id, uploads[0].originalFilename)}
            className="text-blue-700 hover:text-blue-900 font-medium" title={uploads[0].originalFilename}>
            <Download className="w-3 h-3 inline mr-0.5" />
            {uploads[0].originalFilename.length > 10 ? uploads[0].originalFilename.slice(0,10) + '...' : uploads[0].originalFilename}
          </button>
          {hasNew && (
            <span className="ml-1 px-1 py-0.5 bg-green-500 text-white text-xs rounded font-bold leading-none" data-testid={`new-badge-${recordId}-${typeKey}`}>NEW</span>
          )}
          <button onClick={() => onDelete(uploads[0].id, recordId)} className="text-red-400 hover:text-red-600 ml-1">
            <X className="w-3 h-3" />
          </button>
        </span>
      ) : (
        // Multiple uploads — show count badge with expand
        <div className="w-full">
          <button
            onClick={() => setExpanded(v => !v)}
            className="inline-flex items-center gap-1 bg-blue-100 border border-blue-300 rounded-lg px-2 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-200 transition"
            data-testid={`expand-uploads-${recordId}-${typeKey}`}
          >
            <Download className="w-3 h-3" />
            {uploads.length} files
            {hasNew && <span className="px-1 bg-green-500 text-white rounded text-xs font-bold" data-testid={`new-badge-multi-${recordId}-${typeKey}`}>NEW</span>}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {expanded && (
            <div className="mt-1 space-y-1 border border-blue-100 rounded-lg bg-blue-50 p-1.5">
              {uploads.map(u => (
                <div key={u.id} className="flex items-center gap-1 bg-white border border-blue-200 rounded px-1.5 py-0.5">
                  <button onClick={() => onDownload(u.id, u.originalFilename)}
                    className="text-blue-700 hover:text-blue-900 font-medium text-xs flex-1 text-left truncate" title={u.originalFilename}>
                    <Download className="w-3 h-3 inline mr-0.5" />
                    {u.originalFilename.length > 14 ? u.originalFilename.slice(0,14) + '...' : u.originalFilename}
                  </button>
                  {!u.viewedByDirector && (
                    <span className="px-1 bg-green-500 text-white rounded text-xs font-bold flex-shrink-0">NEW</span>
                  )}
                  <button onClick={() => onDelete(u.id, recordId)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Upload button — hidden when hideUpload (e.g. legacy section-level PD row) */}
      {!hideUpload && (
      <div className="flex items-center gap-1">
        <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-900 bg-green-50 px-2 py-1 rounded border border-green-200" data-testid={`upload-${recordId}-${typeKey}`}>
          <Upload className="w-3 h-3" />Upload
          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp"
            onChange={(e) => { if (e.target.files[0]) onUpload(recordId, typeKey, e.target.files[0]); e.target.value=''; }}
            disabled={isUploading} />
        </label>
        {typeKey === 'fireSafetyTraining' && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3.5 h-3.5 text-blue-400 hover:text-blue-600 cursor-help" data-testid={`fire-safety-tooltip-trigger-${recordId}`} />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs p-3 bg-gray-900 text-white rounded-xl">
                <p>A current completion date automatically counts 1 hour toward Professional Development. Please remember to upload the current Fire Safety certificate for your records here.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      )}
    </div>
  );
}

function CourseCertUpload({ recordId, courseId, uploads, isUploading, onUpload, onDownload, onDelete }) {
  const cert = uploads[0];
  return (
    <div className="flex items-center gap-0.5">
      {cert ? (
        <>
          <button type="button" onClick={() => onDownload(cert.id, cert.originalFilename)} title={cert.originalFilename}
            className="h-8 w-8 flex items-center justify-center text-green-600 hover:bg-green-50 rounded" data-testid={`course-cert-download-${courseId}`}>
            <Download className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => onDelete(cert.id, recordId)} title="Remove certificate"
            className="h-8 w-8 flex items-center justify-center text-red-400 hover:bg-red-50 rounded" data-testid={`course-cert-remove-${courseId}`}>
            <X className="w-3.5 h-3.5" />
          </button>
        </>
      ) : (
        <label className="h-8 w-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded cursor-pointer" title="Upload Certificate" data-testid={`course-cert-upload-${courseId}`}>
          <Upload className="w-4 h-4" />
          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp"
            onChange={(e) => { if (e.target.files[0]) onUpload(e.target.files[0]); e.target.value = ''; }}
            disabled={isUploading} />
        </label>
      )}
    </div>
  );
}
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";
import FeatureGate from "@/components/FeatureGate";

// Import modular components
import { StaffSummaryCards, ExpiringClearancesAlert } from "@/components/staff-records";
import { StaffEvaluationTab } from "@/components/staff-reports";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIMARY_OWNER_EMAIL = "todayskidz@comcast.net";

const CLEARANCE_TYPES = [
  { key: "childAbuseClearance", label: "Child Abuse Clearance", shortLabel: "Child Abuse", renewalMonths: 60 },
  { key: "criminalRecordCheck", label: "Criminal Record Check", shortLabel: "Criminal", renewalMonths: 60 },
  { key: "fbiClearance", label: "FBI Clearance", shortLabel: "FBI", renewalMonths: 60 },
  { key: "nsorCheck", label: "NSOR - Report Returned YES, Verified to Work with Children", shortLabel: "NSOR", renewalMonths: 60 },
  { key: "disclosureStatement", label: "Disclosure Statement", shortLabel: "Disclosure", renewalMonths: 0, isOneTime: true },
  { key: "mandatedReporterTraining", label: "Mandated Reporter Training", shortLabel: "Mandated", renewalMonths: 60 },
  { key: "fireSafetyTraining", label: "Fire Safety Training", shortLabel: "Fire Safety", renewalMonths: 12, renewalLabel: "Annually" },
  { key: "emergencyPreparedness", label: "Emergency Preparedness (EPR) Review", shortLabel: "Emergency Preparedness", renewalMonths: 12, renewalLabel: "Annually" },
  { key: "healthSafetyBasics", label: "Health & Safety Basics", shortLabel: "Health/Safety", renewalMonths: 0, isOneTime: true },
  { key: "pediatricFirstAidCPR", label: "Pediatric First Aid & CPR", shortLabel: "CPR/First Aid", renewalMonths: 24 },
  { key: "shakenBabyPolicy", label: "Shaken Baby / Abuse / Head Trauma Policy", shortLabel: "Shaken Baby Policy", renewalMonths: 0, isOneTime: true },
  { key: "profDevTraining", label: "12-Hour Professional Development", shortLabel: "Prof Dev (12hr)", renewalMonths: 12, renewalLabel: "Annually" },
  { key: "staffEvaluation", label: "Staff Performance Evaluation", shortLabel: "Evaluation", renewalMonths: 12, renewalLabel: "Annually" }
];

const StaffRecordsPage = () => {
  const navigate = useNavigate();
  const { user: currentUser, organization } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  
  // Check if current user is owner
  const isOwner = currentUser?.email === PRIMARY_OWNER_EMAIL || currentUser?.role === "owner" || currentUser?.role?.toLowerCase() === "co-owner";
  
  // Filter records - staff only sees their own record
  const filteredRecords = isOwner ? records : records.filter(r => 
    r.email?.toLowerCase() === currentUser?.email?.toLowerCase()
  );
  
  // Alert State
  const [expiringClearances, setExpiringClearances] = useState([]);
  const [alertSettings, setAlertSettings] = useState({
    enabled: false, daysWarning: 30, sendSummary: true, sendIndividual: false, recipientEmail: ""
  });
  const [sendingAlert, setSendingAlert] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  
  // Bulk Upload State
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRecord, setEmailRecord] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailNote, setEmailNote] = useState("");
  const [clearanceUploads, setClearanceUploads] = useState({});
  const [uploadIndicators, setUploadIndicators] = useState({});
  const [uploadMap, setUploadMap] = useState({});
  const [focusClearanceKey, setFocusClearanceKey] = useState(null);
  const [showArchivedProfDev, setShowArchivedProfDev] = useState(false);
  const [plannerRooms, setPlannerRooms] = useState([]);
  useEffect(() => {
    axios.get(`${API}/tour-planner/rooms`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
      .then(res => setPlannerRooms(res.data || []))
      .catch(() => setPlannerRooms([]));
  }, []);
  // Scroll + focus the clearance's date input when dialog opens via badge click
  useEffect(() => {
    if (dialogOpen && focusClearanceKey) {
      const t = setTimeout(() => {
        const el = document.querySelector(`[data-clearance-key="${focusClearanceKey}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          const dateInput = el.querySelector('input[type="date"], input[type="text"]');
          if (dateInput) dateInput.focus();
        }
        setFocusClearanceKey(null);
      }, 400);
      return () => clearTimeout(t);
    }
  }, [dialogOpen, focusClearanceKey]);
  const [uploadingClearance, setUploadingClearance] = useState(null);
  const [sendingLink, setSendingLink] = useState(null);
  // Ref so polling interval always sees latest filteredRecords without stale closure
  const filteredRecordsRef = useRef([]);
  
 
  // Position options
  const POSITION_OPTIONS = [
    { value: "director", label: "Director" },
    { value: "assistant_director", label: "Assistant Director" },
    { value: "group_supervisor", label: "Group Supervisor" },
    { value: "assistant_group_supervisor", label: "Assistant Group Supervisor" },
    { value: "volunteer", label: "Volunteer" },
    { value: "cook", label: "Cook" },
    { value: "custodian", label: "Custodian" },
    { value: "lead_teacher", label: "Lead Teacher" },
    { value: "other", label: "Other (Manual Input)" }
  ];

  // Education options
  const EDUCATION_OPTIONS = [
    { value: "associate_2yr", label: "Degree 2yr Associate" },
    { value: "bachelors_4yr", label: "Degree 4yr Bachelors" },
    { value: "certificate", label: "Certificate" },
    { value: "hs_diploma", label: "H.S. Diploma" },
    { value: "other", label: "Other" }
  ];

  // Form State
  const [formData, setFormData] = useState({
    name: "", email: "", cellPhone: "", birthDate: "", position: "", positionOther: "", plannerCategory: "", plannerCategory2: "", plannerRoomId: "", plannerRoomId2: "", plannerCoverageType2: "different_shift", education: "", educationOther: "",
    startDate: "", lastPhysicalDate: "", nextPhysicalDate: "",
    childAbuseClearance: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 60 },
    criminalRecordCheck: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 60 },
    fbiClearance: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 60 },
    nsorCheck: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 60 },
    disclosureStatement: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 0, isOneTime: true, signedDate: "" },
    mandatedReporterTraining: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 60 },
    fireSafetyTraining: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 12, creditHours: 1 },
    emergencyPreparedness: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 12, orientationDate: "" },
    healthSafetyBasics: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 0, isOneTime: true },
    shakenBabyPolicy: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 0, isOneTime: true },
    pediatricFirstAidCPR: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 24, creditHours: "" },
    profDevTraining: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 12, courses: [] },
    staffEvaluation: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 12 },
    tbTest: { testDate: "", result: "", explanation: "" },
    annualProfDev: { year: new Date().getFullYear(), hoursCompleted: 0, courses: [] },
    notes: "", isActive: true, exemptFromEvaluation: false
  });

  const isPrimaryOwner = currentUser?.email?.toLowerCase() === PRIMARY_OWNER_EMAIL.toLowerCase() || currentUser?.isPrimaryOwner;

  // ============== DATA FETCHING ==============
  
  const fetchRecords = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/staff-records`);
      setRecords(response.data.records || []);
    } catch (error) {
      console.error("Error fetching records:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied");
        navigate("/");
      } else {
        toast.error("Failed to load staff records");
      }
    }
    setLoading(false);
  };

  const fetchExpiringClearances = async () => {
    try {
      const response = await axios.get(`${API}/staff-records/expiring-clearances?days=${alertSettings.daysWarning}`);
      // Map API response to component expected format
      const mapped = (response.data.expiring_staff || []).map(staff => ({
        ...staff,
        name: staff.staff_name,
        email: staff.staff_email,
        clearances: staff.clearances.map(c => ({
          ...c,
          type: c.clearance_name,
          expiration_date: c.expiration_date,
          days_until_expiry: c.days_until_expiry
        }))
      }));
      setExpiringClearances(mapped);
    } catch (error) {
      console.error("Error fetching expiring clearances:", error);
    }
  };

  const fetchAlertSettings = async () => {
    try {
      const response = await axios.get(`${API}/staff-records/alert-settings`);
      setAlertSettings({
        enabled: response.data.enabled || false,
        daysWarning: response.data.daysWarning || 30,
        sendSummary: response.data.sendSummary !== false,
        sendIndividual: response.data.sendIndividual || false,
        recipientEmail: response.data.recipientEmail || currentUser?.email || ""
      });
    } catch (error) {
      console.error("Error fetching alert settings:", error);
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchAlertSettings();
    fetchExpiringClearances();
    fetchUploadIndicators();
  }, []);

  // Keep ref in sync with latest filteredRecords for the polling interval
  useEffect(() => {
    filteredRecordsRef.current = filteredRecords;
  }, [filteredRecords]);

  // Always re-fetch uploads when visible records change (no stale-cache guard)
  useEffect(() => {
    filteredRecords.forEach(r => fetchUploadsForRecord(r.id));
  }, [filteredRecords.length]);

  // Auto-poll every 90 seconds — picks up staff-uploaded files without page reload
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUploadIndicators();
      filteredRecordsRef.current.forEach(r => fetchUploadsForRecord(r.id));
    }, 90000);
    return () => clearInterval(interval);
  }, []);

  // ============== FORM HANDLERS ==============
  
  const resetForm = () => {
    setFormData({
      name: "", email: "", cellPhone: "", birthDate: "", position: "", positionOther: "", plannerCategory: "", plannerCategory2: "", plannerRoomId: "", plannerRoomId2: "", plannerCoverageType2: "different_shift", education: "", educationOther: "",
      startDate: "", lastPhysicalDate: "", nextPhysicalDate: "",
      childAbuseClearance: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 60 },
      criminalRecordCheck: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 60 },
      fbiClearance: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 60 },
      nsorCheck: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 60 },
      disclosureStatement: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 0, isOneTime: true, signedDate: "" },
      mandatedReporterTraining: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 60 },
      fireSafetyTraining: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 12, creditHours: 1 },
      emergencyPreparedness: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 12, orientationDate: "" },
      healthSafetyBasics: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 0, isOneTime: true },
      shakenBabyPolicy: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 0, isOneTime: true },
      pediatricFirstAidCPR: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 24, creditHours: "" },
      profDevTraining: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 12, courses: [] },
    staffEvaluation: { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: 12 },
      tbTest: { testDate: "", result: "", explanation: "" },
      annualProfDev: { year: new Date().getFullYear(), hoursCompleted: 0, courses: [] },
      notes: "", isActive: true, exemptFromEvaluation: false
    });
    setEditingRecord(null);
  };

  // Helper function to normalize date to YYYY-MM-DD format for HTML date input
  const normalizeDateForInput = (dateStr) => {
    if (!dateStr || dateStr === 'nan' || dateStr === 'null') return "";
    
    try {
      // Already in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      
      // Handle MM/DD/YYYY format (from bulk upload)
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const [month, day, year] = parts;
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      
      // Handle numeric format like 8182025 (MMDDYYYY)
      if (typeof dateStr === 'number' || /^\d+\.?\d*$/.test(String(dateStr))) {
        let numStr = String(dateStr).split('.')[0];
        if (numStr.length === 7) numStr = '0' + numStr;
        if (numStr.length === 8) {
          const month = numStr.substring(0, 2);
          const day = numStr.substring(2, 4);
          const year = numStr.substring(4, 8);
          return `${year}-${month}-${day}`;
        }
      }
      
      // Try parseISO as fallback
      const parsed = parseISO(dateStr);
      if (!isNaN(parsed.getTime())) {
        return format(parsed, 'yyyy-MM-dd');
      }
      
      return "";
    } catch (e) {
      console.error("Error normalizing date:", dateStr, e);
      return "";
    }
  };

  // Professional Development course archive/restore/delete — immediate backend calls
  // so currentCycleHours updates right away, same source used by Hub/Dashboard.
  const archiveProfDevCourse = async (courseId) => {
    if (!editingRecord?.id) return;
    try {
      const res = await axios.patch(`${API}/staff-records/${editingRecord.id}/prof-dev-courses/${courseId}/archive`);
      setFormData(prev => ({ ...prev, profDevTraining: res.data.profDevTraining }));
      toast.success("Course archived");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not archive course");
    }
  };

  const restoreProfDevCourse = async (courseId) => {
    if (!editingRecord?.id) return;
    try {
      const res = await axios.patch(`${API}/staff-records/${editingRecord.id}/prof-dev-courses/${courseId}/restore`);
      setFormData(prev => ({ ...prev, profDevTraining: res.data.profDevTraining }));
      toast.success("Course restored");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not restore course");
    }
  };

  const deleteProfDevCourse = async (courseId) => {
    if (!editingRecord?.id) return;
    if (!window.confirm("This will permanently delete this course. This action cannot be undone. Continue?")) return;
    try {
      const res = await axios.delete(`${API}/staff-records/${editingRecord.id}/prof-dev-courses/${courseId}`);
      setFormData(prev => ({ ...prev, profDevTraining: res.data.profDevTraining }));
      toast.success("Course deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete course");
    }
  };


  // Helper function to auto-calculate expiration date if missing but completion date exists
  const ensureExpirationDate = (clearanceData, defaultRenewalMonths) => {
    if (!clearanceData) {
      return { status: "pending", completionDate: "", expirationDate: "", renewalPeriodMonths: defaultRenewalMonths };
    }
    
    const data = { ...clearanceData };
    
    // Normalize dates to YYYY-MM-DD format
    if (data.completionDate) {
      data.completionDate = normalizeDateForInput(data.completionDate);
    }
    if (data.expirationDate) {
      data.expirationDate = normalizeDateForInput(data.expirationDate);
    }
    if (data.signedDate) {
      data.signedDate = normalizeDateForInput(data.signedDate);
    }
    
    // Ensure renewalPeriodMonths is set
    if (!data.renewalPeriodMonths && defaultRenewalMonths) {
      data.renewalPeriodMonths = defaultRenewalMonths;
    }
    
    // If there's a completion date but no expiration date, calculate it
    if (data.completionDate && !data.expirationDate && data.renewalPeriodMonths > 0) {
      try {
        const completionDate = parseISO(data.completionDate);
        const expiration = addMonths(completionDate, data.renewalPeriodMonths);
        data.expirationDate = format(expiration, 'yyyy-MM-dd');
      } catch (e) {
        console.error("Error calculating expiration date:", e);
      }
    }
    
    // Auto-calculate status based on dates
    if (data.completionDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (data.isOneTime) {
        data.status = "completed";
      } else if (data.expirationDate) {
        const expDate = parseISO(data.expirationDate);
        expDate.setHours(0, 0, 0, 0);
        data.status = today > expDate ? "expired" : "completed";
      } else {
        data.status = "completed";
      }
    } else {
      data.status = "pending";
    }
    
    return data;
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    
    // Normalize TB test date
    const normalizedTbTest = record.tbTest 
      ? { ...record.tbTest, testDate: normalizeDateForInput(record.tbTest.testDate) }
      : { testDate: "", result: "", explanation: "" };
    
    setFormData({
      name: record.name || "",
      email: record.email || "",
      cellPhone: record.cellPhone || "",
      birthDate: normalizeDateForInput(record.birthDate),
      position: record.position || "",
      positionOther: record.positionOther || "",
      plannerCategory: record.plannerCategory || "",
      plannerCategory2: record.plannerCategory2 || "",
      plannerRoomId: record.plannerRoomId || "",
      plannerRoomId2: record.plannerRoomId2 || "",
      plannerCoverageType2: record.plannerCoverageType2 || "different_shift",
      education: record.education || "",
      educationOther: record.educationOther || "",
      startDate: normalizeDateForInput(record.startDate),
      lastPhysicalDate: normalizeDateForInput(record.lastPhysicalDate),
      nextPhysicalDate: (() => {
        if (record.nextPhysicalDate) return normalizeDateForInput(record.nextPhysicalDate);
        if (record.lastPhysicalDate) {
          try {
            const last = parseISO(normalizeDateForInput(record.lastPhysicalDate));
            return format(addMonths(last, 24), 'yyyy-MM-dd');
          } catch { return ""; }
        }
        return "";
      })(),
      childAbuseClearance: ensureExpirationDate(record.childAbuseClearance, 60),
      criminalRecordCheck: ensureExpirationDate(record.criminalRecordCheck, 60),
      fbiClearance: ensureExpirationDate(record.fbiClearance, 60),
      nsorCheck: ensureExpirationDate(record.nsorCheck, 60),
      disclosureStatement: ensureExpirationDate({ ...record.disclosureStatement, isOneTime: true }, 0),
      mandatedReporterTraining: ensureExpirationDate(record.mandatedReporterTraining, 60),
      fireSafetyTraining: ensureExpirationDate(record.fireSafetyTraining, 12),
      emergencyPreparedness: ensureExpirationDate(record.emergencyPreparedness, 12),
      healthSafetyBasics: ensureExpirationDate({ ...(record.healthSafetyBasics || record.healthHumanServices), isOneTime: true }, 0),
      pediatricFirstAidCPR: { ...ensureExpirationDate(record.pediatricFirstAidCPR, 24), creditHours: record.pediatricFirstAidCPR?.creditHours || "" },
      shakenBabyPolicy: ensureExpirationDate({ ...record.shakenBabyPolicy, isOneTime: true }, 0),
      profDevTraining: ensureExpirationDate(record.profDevTraining, 12),
      staffEvaluation: ensureExpirationDate(record.staffEvaluation, 12),
      tbTest: normalizedTbTest,
      annualProfDev: record.annualProfDev || { year: new Date().getFullYear(), hoursCompleted: 0, courses: [] },
      notes: record.notes || "",
      isActive: record.isActive !== false,
      exemptFromEvaluation: record.exemptFromEvaluation || false
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation - name required for all, start date required only for owner
    if (!formData.name) {
      toast.error("Name is required");
      return;
    }
    if (isOwner && !formData.startDate) {
      toast.error("Start date is required");
      return;
    }

    // Validate prof dev courses — completion date is required for every manually entered course
    const manualCourses = (formData.profDevTraining?.courses || []).filter(c => !c.autoSource);
    const missingDate = manualCourses.find(c => c.courseName && !c.dateCompleted);
    if (missingDate) {
      toast.error(`Completion date is required for "${missingDate.courseName || 'a course'}". Please add a date before saving.`);
      return;
    }

    // For non-owners, ensure their email is set from login
    const submitData = { ...formData };
    if (!isOwner && !submitData.email) {
      submitData.email = currentUser?.email || '';
    }

    try {
      if (editingRecord) {
        const response = await axios.put(`${API}/staff-records/${editingRecord.id}`, submitData);
        toast.success("Staff record updated");
        if (response.data?.autoSynced?.length > 0) {
          response.data.autoSynced.forEach(item => {
            toast.success(`${item.displayName} — ${item.hours} hr${item.hours !== 1 ? 's' : ''} auto-added to Prof Dev 12hr log`);
          });
        }
      } else {
        await axios.post(`${API}/staff-records`, submitData);
        toast.success("Staff record created");
      }
      setDialogOpen(false);
      resetForm();
      fetchRecords();
    } catch (error) {
      console.error("Error saving record:", error);
      toast.error(error.response?.data?.detail || "Failed to save record");
    }
  };

  const handleDelete = async (recordId) => {
    if (window.confirm("Are you sure you want to delete this staff record?")) {
      try {
        await axios.delete(`${API}/staff-records/${recordId}`);
        toast.success("Staff record deleted");
        fetchRecords();
      } catch (error) {
        console.error("Error deleting record:", error);
        toast.error("Failed to delete record");
      }
    }
  };

  const updateClearance = (clearanceKey, field, value) => {
    const current = formData[clearanceKey] || {};
    const updated = { ...current, [field]: value };
    
    // Auto-calculate expiration date when completion date is entered
    if (field === "completionDate" && value && updated.renewalPeriodMonths) {
      const completionDate = parseISO(value);
      const expiration = addMonths(completionDate, updated.renewalPeriodMonths);
      updated.expirationDate = format(expiration, 'yyyy-MM-dd');
    }
    
    // Recalculate expiration when renewal period is changed
    if (field === "renewalPeriodMonths" && updated.completionDate && value > 0) {
      const completionDate = parseISO(updated.completionDate);
      const expiration = addMonths(completionDate, parseInt(value));
      updated.expirationDate = format(expiration, 'yyyy-MM-dd');
    }
    
    // Auto-calculate status based on dates
    if (field === "completionDate" || field === "expirationDate" || field === "renewalPeriodMonths") {
      updated.status = calculateStatus(updated.completionDate, updated.expirationDate, current.isOneTime);
    }
    
    // If status manually set to completed without dates, set today's date
    if (field === "status" && value === "completed" && !updated.completionDate) {
      updated.completionDate = format(new Date(), 'yyyy-MM-dd');
      if (updated.renewalPeriodMonths) {
        const expiration = addMonths(new Date(), updated.renewalPeriodMonths);
        updated.expirationDate = format(expiration, 'yyyy-MM-dd');
      }
    }
    
    setFormData({ ...formData, [clearanceKey]: updated });
  };

  // Auto-calculate status based on completion and expiration dates
  const calculateStatus = (completionDate, expirationDate, isOneTime) => {
    if (!completionDate) return "not on file";
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // If one-time (like Disclosure), completed once = always completed
    if (isOneTime) return "completed";
    
    // Check if expired
    if (expirationDate) {
      const expDate = parseISO(expirationDate);
      expDate.setHours(0, 0, 0, 0);
      if (today > expDate) return "expired";
    }
    
    return "completed";
  };

  // Handle physical date change - auto-generate next physical date (2 years)
  const handlePhysicalDateChange = (value) => {
    const updates = { lastPhysicalDate: value };
    if (value) {
      const lastDate = parseISO(value);
      const nextDate = addMonths(lastDate, 24); // 2 years = 24 months
      updates.nextPhysicalDate = format(nextDate, 'yyyy-MM-dd');
    }
    setFormData({ ...formData, ...updates });
  };

  // Get physical status based on dates
  const getPhysicalStatus = () => {
    if (!formData.lastPhysicalDate) return "pending";
    if (!formData.nextPhysicalDate) return "completed";
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextDate = parseISO(formData.nextPhysicalDate);
    nextDate.setHours(0, 0, 0, 0);
    
    if (today > nextDate) return "expired";
    return "completed";
  };

  const sendExpirationAlerts = async () => {
    setSendingAlert(true);
    try {
      const response = await axios.post(`${API}/staff-records/send-expiration-alerts`, {
        days: alertSettings.daysWarning,
        send_summary: alertSettings.sendSummary,
        send_individual: alertSettings.sendIndividual,
        recipient_email: alertSettings.recipientEmail
      });
      
      if (response.data.emails_sent > 0) {
        toast.success(`Sent ${response.data.emails_sent} alert email(s)`);
      } else {
        toast.info("No expiring clearances found");
      }
      setAlertDialogOpen(false);
    } catch (error) {
      console.error("Error sending alerts:", error);
      toast.error("Failed to send alert emails");
    } finally {
      setSendingAlert(false);
    }
  };

  // Send alert directly to staff members with expiring clearances
  const sendAlertsToStaff = async (selectedStaff = null) => {
    setSendingAlert(true);
    try {
      // If selectedStaff is provided, only send to those staff members
      const staffEmails = selectedStaff ? selectedStaff.map(s => s.email).filter(Boolean) : null;
      
      const response = await axios.post(`${API}/staff-records/send-expiration-alerts`, {
        days: alertSettings.daysWarning,
        send_summary: false,
        send_individual: true,
        recipient_email: currentUser?.email || '',
        staff_emails: staffEmails  // Pass specific staff emails if selected
      });
      
      if (response.data.emails_sent > 0 || response.data.staff_emails_sent > 0) {
        const count = response.data.emails_sent || response.data.staff_emails_sent;
        toast.success(`Sent alerts to ${count} staff member(s)`);
      } else {
        toast.info("No staff emails could be sent - check staff email addresses");
      }
    } catch (error) {
      console.error("Error sending staff alerts:", error);
      toast.error("Failed to send staff alerts");
    } finally {
      setSendingAlert(false);
    }
  };

  // Email staff record report
  const handleEmailReport = (record) => {
    setEmailRecord(record);
    setEmailTo(record.email || "");
    setEmailNote("");
    setEmailDialogOpen(true);
  };

  const sendEmailReport = async () => {
    if (!emailTo || !emailRecord) return;
    try {
      await axios.post(`${API}/staff-records/${emailRecord.id}/email-report`, {
        email: emailTo,
        note: emailNote
      });
      toast.success(`Report emailed to ${emailTo}`);
      setEmailDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to email report");
    }
  };

  // ============== CLEARANCE UPLOADS ==============

  const fetchUploadIndicators = async () => {
    try {
      const res = await axios.get(`${API}/staff-records/upload-indicators`);
      setUploadIndicators(res.data.indicators || {});
      setUploadMap(res.data.uploadMap || {});
    } catch (e) { /* ignore */ }
  };

  const fetchUploadsForRecord = async (recordId) => {
    try {
      const res = await axios.get(`${API}/staff-records/${recordId}/clearance-uploads`);
      setClearanceUploads(prev => ({ ...prev, [recordId]: res.data.uploads || [] }));
    } catch (e) { /* ignore */ }
  };

  const handleClearanceUpload = async (recordId, clearanceType, file, courseId = null) => {
    const uploadingKey = courseId ? `${recordId}-${clearanceType}-${courseId}` : `${recordId}-${clearanceType}`;
    setUploadingClearance(uploadingKey);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("clearance_type", clearanceType);
    if (courseId) formData.append("course_id", courseId);
    try {
      await axios.post(`${API}/staff-records/${recordId}/clearance-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(isOwner ? "Document uploaded" : "Awaiting Director Review");
      fetchUploadsForRecord(recordId);
      fetchUploadIndicators();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingClearance(null);
    }
  };

  const handleDeleteUpload = async (fileId, recordId) => {
    try {
      await axios.delete(`${API}/clearance-uploads/${fileId}`);
      // Immediately remove file from UI state — do not wait for refetch
      setClearanceUploads(prev => ({
        ...prev,
        [recordId]: (prev[recordId] || []).filter(u => u.id !== fileId)
      }));
      toast.success("Document removed");
      fetchUploadsForRecord(recordId);
      fetchUploadIndicators();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove document");
    }
  };

  const sendClearanceLink = async (recordId, clearanceType, staffName) => {
    setSendingLink(`${recordId}-${clearanceType}`);
    try {
      await axios.post(`${API}/staff-records/${recordId}/send-clearance-upload-link/${clearanceType}`);
      toast.success(`Upload link sent to ${staffName}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send upload link");
    } finally {
      setSendingLink(null);
    }
  };

  const handleDownloadUpload = async (fileId, filename) => {    try {
      const res = await axios.get(`${API}/clearance-uploads/${fileId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      // For PDFs/images open in new tab, otherwise download
      if (res.data.type.startsWith('image/') || res.data.type === 'application/pdf') {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'document';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Mark as viewed in local state immediately so NEW badge clears without waiting for next poll
      setClearanceUploads(prev => {
        const next = {};
        for (const rid in prev) {
          next[rid] = prev[rid].map(u => u.id === fileId ? { ...u, viewedByDirector: true } : u);
        }
        return next;
      });
      fetchUploadIndicators();
    } catch (e) {
      toast.error("Failed to download document");
    }
  };

  const handlePrintUpload = async (fileId) => {
    try {
      const res = await axios.get(`${API}/clearance-uploads/${fileId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      const win = window.open(url, '_blank');
      if (win) {
        win.addEventListener('load', () => win.print());
      }
    } catch (e) {
      toast.error("Failed to open document for printing");
    }
  };

  // ============== HELPERS ==============
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "Not Entered";
    try {
      // Handle numeric values like 4282016.0 or 8302022
      if (typeof dateStr === 'number' || (typeof dateStr === 'string' && /^\d+\.?\d*$/.test(dateStr))) {
        let numStr = String(dateStr).split('.')[0]; // Remove decimal
        
        // Pad to 8 digits if 7 digits (e.g., 4282016 -> 04282016)
        if (numStr.length === 7) {
          numStr = '0' + numStr;
        }
        
        if (numStr.length === 8) {
          const month = numStr.substring(0, 2);
          const day = numStr.substring(2, 4);
          const year = numStr.substring(4, 8);
          return format(new Date(year, parseInt(month) - 1, day), 'MMM d, yyyy');
        }
      }
      
      // Handle MM/DD/YYYY format from bulk upload
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        const [month, day, year] = dateStr.split('/');
        return format(new Date(year, month - 1, day), 'MMM d, yyyy');
      }
      // Handle YYYY-MM-DD format
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch { 
      return dateStr; // Return as-is if parsing fails
    }
  };

  const calculateTenure = (startDate) => {
    if (!startDate) return "N/A";
    try {
      let start;
      
      // Handle numeric values like 4282016.0 or 8302022
      if (typeof startDate === 'number' || (typeof startDate === 'string' && /^\d+\.?\d*$/.test(startDate))) {
        let numStr = String(startDate).split('.')[0]; // Remove decimal
        
        // Pad to 8 digits if 7 digits (e.g., 4282016 -> 04282016)
        if (numStr.length === 7) {
          numStr = '0' + numStr;
        }
        
        if (numStr.length === 8) {
          const month = parseInt(numStr.substring(0, 2));
          const day = parseInt(numStr.substring(2, 4));
          const year = parseInt(numStr.substring(4, 8));
          start = new Date(year, month - 1, day);
        } else {
          return "N/A";
        }
      }
      // Handle MM/DD/YYYY format
      else if (typeof startDate === 'string' && startDate.includes('/')) {
        const [month, day, year] = startDate.split('/');
        start = new Date(year, month - 1, day);
      } else {
        start = parseISO(startDate);
      }
      
      const now = new Date();
      const years = Math.floor((now - start) / (365.25 * 24 * 60 * 60 * 1000));
      const months = Math.floor(((now - start) % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000));
      return years > 0 ? `${years}y ${months}m` : `${months}m`;
    } catch { return "N/A"; }
  };

  const getStatusBadge = (clearance, calculatedExpiration = null, isOneTime = false, hasUpload = false, onClick = null) => {
    if (!clearance || !clearance.completionDate) {
      if (hasUpload) {
        return (
          <Badge
            className="bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200"
            title="A file was uploaded but no completion date is on the record yet. Click to enter the date from the form."
            onClick={onClick || undefined}
            data-testid="badge-uploaded-date-needed"
          >
            📎 Uploaded — date needed
          </Badge>
        );
      }
      return <Badge className="bg-amber-100 text-amber-700">Not on file</Badge>;
    }
    if (isOneTime && clearance.completionDate) {
      return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
    }
    
    // Use calculated expiration if provided, otherwise use stored expiration
    const expirationToCheck = calculatedExpiration || clearance.expirationDate;
    
    // Check if we have a completion date and an expiration to evaluate
    if (clearance.completionDate && expirationToCheck) {
      try {
        let expDate;
        const expStr = expirationToCheck;
        
        // Handle numeric format
        if (typeof expStr === 'number' || /^\d+\.?\d*$/.test(expStr)) {
          let numStr = String(expStr).split('.')[0];
          if (numStr.length === 7) numStr = '0' + numStr;
          if (numStr.length === 8) {
            const month = parseInt(numStr.substring(0, 2)) - 1;
            const day = parseInt(numStr.substring(2, 4));
            const year = parseInt(numStr.substring(4, 8));
            expDate = new Date(year, month, day);
          }
        }
        // Handle MM/DD/YYYY format
        else if (typeof expStr === 'string' && expStr.includes('/')) {
          const [month, day, year] = expStr.split('/');
          expDate = new Date(year, month - 1, day);
        }
        // Handle ISO format (YYYY-MM-DD)
        else {
          expDate = parseISO(expStr);
        }
        
        if (expDate && !isNaN(expDate.getTime())) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          expDate.setHours(0, 0, 0, 0);
          
          const daysUntil = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
          if (daysUntil < 0) {
            if (hasUpload) {
              return (
                <Badge
                  className="bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200"
                  title="Expired, but a file was uploaded. Click to review and enter the new date."
                  onClick={onClick || undefined}
                  data-testid="badge-expired-awaiting-review"
                >
                  📎 Expired — Awaiting Review
                </Badge>
              );
            }
            return <Badge className="bg-red-100 text-red-800">Expired</Badge>;
          }
          if (daysUntil <= 30) {
            if (hasUpload) {
              return (
                <Badge
                  className="bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200"
                  title="Expiring soon, but a file was uploaded. Click to review and enter the new date."
                  onClick={onClick || undefined}
                  data-testid="badge-expiring-awaiting-review"
                >
                  📎 Expiring — Awaiting Review
                </Badge>
              );
            }
            return <Badge className="bg-orange-100 text-orange-800">Expiring Soon</Badge>;
          }
          return <Badge className="bg-green-100 text-green-800">Current</Badge>;
        }
      } catch (e) {
        console.error('Error parsing expiration date:', e);
      }
    }
    
    // If has completion date but no expiration (one-time or error), show as Current/Completed
    if (clearance.completionDate) {
      return <Badge className="bg-green-100 text-green-800">Current</Badge>;
    }
    
    // No completion date = Not on file
    return <Badge className="bg-amber-100 text-amber-700">Not on file</Badge>;
  };

  const handlePrintRecord = (record) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Popup blocked! Please allow popups for this site to print records.");
      return;
    }

    const getClearanceStatus = (clearance, isOneTime, renewalMonths = 0) => {
      if (!clearance?.completionDate) return { status: 'Not on file', color: '#9CA3AF', bg: '#F3F4F6' };
      const today = new Date(); today.setHours(0,0,0,0);
      if (isOneTime) return { status: 'Completed', color: '#059669', bg: '#ECFDF5' };
      let expDate = null;
      if (clearance.expirationDate) {
        expDate = new Date(clearance.expirationDate);
      } else if (clearance.completionDate && renewalMonths > 0) {
        let compDate;
        const dateStr = clearance.completionDate;
        if (typeof dateStr === 'number' || /^\d+\.?\d*$/.test(dateStr)) {
          let numStr = String(dateStr).split('.')[0];
          if (numStr.length === 7) numStr = '0' + numStr;
          if (numStr.length === 8) {
            compDate = new Date(parseInt(numStr.substring(4,8)), parseInt(numStr.substring(0,2))-1, parseInt(numStr.substring(2,4)));
          }
        } else if (typeof dateStr === 'string' && dateStr.includes('/')) {
          const [m,d,yr] = dateStr.split('/');
          compDate = new Date(yr, m-1, d);
        } else { compDate = new Date(dateStr); }
        if (compDate && !isNaN(compDate.getTime())) {
          expDate = new Date(compDate);
          expDate.setMonth(expDate.getMonth() + renewalMonths);
        }
      }
      if (expDate && !isNaN(expDate.getTime())) {
        expDate.setHours(0,0,0,0);
        if (today > expDate) return { status: 'Expired', color: '#DC2626', bg: '#FEF2F2' };
        const daysUntil = Math.ceil((expDate - today) / 86400000);
        if (daysUntil <= 30) return { status: 'Expiring Soon', color: '#D97706', bg: '#FFFBEB' };
      }
      return { status: 'Current', color: '#059669', bg: '#ECFDF5' };
    };

    const clearances = [
      { name: 'Child Abuse Clearance', data: record.childAbuseClearance, renewalMonths: 60 },
      { name: 'Criminal Record Check', data: record.criminalRecordCheck, renewalMonths: 60 },
      { name: 'FBI Clearance', data: record.fbiClearance, renewalMonths: 60 },
      { name: 'NSOR Check', data: record.nsorCheck, renewalMonths: 60 },
      { name: 'Disclosure Statement', data: record.disclosureStatement, isOneTime: true, renewalMonths: 0 },
      { name: 'Mandated Reporter Training', data: record.mandatedReporterTraining, renewalMonths: 60 },
      { name: 'Fire Safety Training', data: record.fireSafetyTraining, renewalMonths: 12 },
      { name: 'Emergency Preparedness', data: record.emergencyPreparedness, renewalMonths: 12 },
      { name: 'Health & Safety Basics', data: record.healthSafetyBasics || record.healthHumanServices, isOneTime: true, renewalMonths: 0 },
      { name: 'Pediatric First Aid & CPR', data: record.pediatricFirstAidCPR, renewalMonths: 24 },
      { name: 'Prof. Development (12hr)', data: record.profDevTraining, renewalMonths: 12 },
      { name: 'Staff Performance Evaluation', data: record.staffEvaluation, renewalMonths: 12 }
    ];

    const calcExp = (c) => {
      if (c.data?.expirationDate) return formatDate(c.data.expirationDate);
      if (c.data?.completionDate && c.renewalMonths > 0) {
        try {
          const d = new Date(c.data.completionDate);
          if (!isNaN(d.getTime())) { d.setMonth(d.getMonth() + c.renewalMonths); return d.toLocaleDateString('en-US'); }
        } catch(e) {}
      }
      if (!c.data?.completionDate) return 'Not On File';
      return c.isOneTime ? 'N/A' : 'Not On File';
    };

    printWindow.document.write(`<!DOCTYPE html><html><head><title>Staff Record - ${record.name}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',system-ui,-apple-system,sans-serif;color:#1a1a1a;padding:40px 50px;max-width:850px;margin:0 auto;font-size:13px;line-height:1.4}
.header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1e3a5f;padding-bottom:16px;margin-bottom:24px}
.header h1{font-size:22px;font-weight:700;color:#1e3a5f;letter-spacing:-0.5px}
.header .subtitle{font-size:12px;color:#6b7280;margin-top:2px}
.header .org{font-size:14px;color:#374151;font-weight:600}
.logo{height:50px;width:auto;margin-bottom:8px}
.section{margin-bottom:20px}
.section-title{font-size:13px;font-weight:700;color:#1e3a5f;text-transform:uppercase;letter-spacing:0.5px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-bottom:10px}
.grid{display:grid;gap:8px 24px}
.grid-2{grid-template-columns:1fr 1fr}
.grid-3{grid-template-columns:1fr 1fr 1fr}
.field .label{font-size:10px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.3px}
.field .value{font-size:13px;color:#111827;font-weight:500;margin-top:1px}
table{width:100%;border-collapse:collapse;font-size:12px}
th{background:#f8fafc;color:#374151;font-weight:600;text-align:left;padding:8px 10px;border-bottom:2px solid #e5e7eb}
td{padding:7px 10px;border-bottom:1px solid #f3f4f6}
tr:hover{background:#fafafa}
.badge{display:inline-block;padding:2px 8px;border-radius:3px;font-size:11px;font-weight:600}
.footer{margin-top:30px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af;text-align:center}
@media print{body{padding:20px 30px}@page{margin:0.5in}}
</style></head><body>
<div class="header">
  <div><img src="/simple-sign-in-logo.png" class="logo" alt="Simple Sign In" onerror="this.style.display='none'" /><h1>${record.name}</h1><div class="subtitle">Staff Compliance Record</div></div>
  <div style="text-align:right"><div class="org">${organization?.name || ''}</div><div class="subtitle">${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</div></div>
</div>
<div class="section">
  <div class="section-title">Staff Information</div>
  <div class="grid grid-3">
    <div class="field"><div class="label">Email</div><div class="value">${record.email || '-'}</div></div>
    <div class="field"><div class="label">Position</div><div class="value">${record.position ? (POSITION_OPTIONS.find(p=>p.value===record.position)?.label || record.position) : '-'}</div></div>
    <div class="field"><div class="label">Start Date</div><div class="value">${formatDate(record.startDate)}</div></div>
    <div class="field"><div class="label">Birth Date</div><div class="value">${formatDate(record.birthDate)}</div></div>
    <div class="field"><div class="label">Education</div><div class="value">${record.education ? (EDUCATION_OPTIONS.find(e=>e.value===record.education)?.label || record.education) : '-'}</div></div>
    <div class="field"><div class="label">Status</div><div class="value">${record.isActive !== false ? 'Active' : 'Inactive'}</div></div>
  </div>
</div>
<div class="section">
  <div class="section-title">Health Records</div>
  <div class="grid grid-2" style="grid-template-columns:1fr 1fr 1fr 1fr">
    <div class="field"><div class="label">Last Physical</div><div class="value">${formatDate(record.lastPhysicalDate)}</div></div>
    <div class="field"><div class="label">Next Physical</div><div class="value">${formatDate(record.nextPhysicalDate)}</div></div>
    <div class="field"><div class="label">TB Test</div><div class="value">${formatDate(record.tbTest?.testDate)}</div></div>
    <div class="field"><div class="label">TB Result</div><div class="value">${record.tbTest?.result ? record.tbTest.result.charAt(0).toUpperCase()+record.tbTest.result.slice(1) : '-'}</div></div>
  </div>
</div>
<div class="section">
  <div class="section-title">Clearances & Certifications</div>
  <table>
    <thead><tr><th>Type</th><th>Status</th><th>Completed</th><th>Expires</th><th>Renewal</th></tr></thead>
    <tbody>${clearances.map(c => {
      const s = getClearanceStatus(c.data, c.isOneTime, c.renewalMonths);
      return `<tr><td>${c.name}${c.isOneTime ? ' <span style="color:#9ca3af;font-style:italic">(One-Time)</span>':''}</td><td><span class="badge" style="background:${s.bg};color:${s.color}">${s.status}</span></td><td>${formatDate(c.data?.completionDate)}</td><td>${calcExp(c)}</td><td>${c.isOneTime ? '-' : c.renewalMonths + ' mo'}</td></tr>`;
    }).join('')}</tbody>
  </table>
</div>
${record.notes ? `<div class="section"><div class="section-title">Notes</div><p style="color:#374151">${record.notes}</p></div>` : ''}
<div class="footer">Confidential Staff Record &mdash; ${organization?.name || ''} &mdash; Printed ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</div>
<script>window.onload=function(){window.print()}</script></body></html>`);
    printWindow.document.close();
  };

  // Generate Summary Report PDF for all staff (Owner only)
  const generateSummaryReport = () => {
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      toast.error("Popup blocked! Please allow popups for this site to generate reports.");
      return;
    }
    
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    // Helper to parse dates in various formats
    const parseAnyDate = (dateStr) => {
      if (!dateStr) return null;
      try {
        if (typeof dateStr === 'number' || /^\d+\.?\d*$/.test(dateStr)) {
          let numStr = String(dateStr).split('.')[0];
          if (numStr.length === 7) numStr = '0' + numStr;
          if (numStr.length === 8) {
            const month = parseInt(numStr.substring(0, 2)) - 1;
            const day = parseInt(numStr.substring(2, 4));
            const year = parseInt(numStr.substring(4, 8));
            return new Date(year, month, day);
          }
        } else if (typeof dateStr === 'string' && dateStr.includes('/')) {
          const [month, day, year] = dateStr.split('/');
          return new Date(year, month - 1, day);
        } else {
          return new Date(dateStr);
        }
      } catch (e) {}
      return null;
    };
    
    // Calculate expiration from completion date
    const calculateExpiration = (completionDate, renewalMonths) => {
      if (!completionDate || !renewalMonths) return null;
      const compDate = parseAnyDate(completionDate);
      if (!compDate || isNaN(compDate.getTime())) return null;
      const expDate = new Date(compDate);
      expDate.setMonth(expDate.getMonth() + renewalMonths);
      return expDate;
    };
    
    const getClearanceStatus = (clearance, isOneTime, renewalMonths = 0) => {
      if (!clearance?.completionDate) return { status: 'Not on file', color: '#EAB308', bg: '#FEF3C7' };
      if (isOneTime) return { status: 'Completed', color: '#22C55E', bg: '#DCFCE7' };
      
      // Calculate or use stored expiration
      let expDate = clearance.expirationDate ? parseAnyDate(clearance.expirationDate) : null;
      if (!expDate && clearance.completionDate && renewalMonths > 0) {
        expDate = calculateExpiration(clearance.completionDate, renewalMonths);
      }
      
      if (expDate && !isNaN(expDate.getTime())) {
        expDate.setHours(0, 0, 0, 0);
        const days = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
        if (days < 0) return { status: 'Expired', color: '#EF4444', bg: '#FEE2E2' };
        if (days <= 30) return { status: 'Expiring', color: '#F59E0B', bg: '#FEF3C7' };
      }
      return { status: 'Current', color: '#22C55E', bg: '#DCFCE7' };
    };

    const clearanceTypes = [
      { key: 'childAbuseClearance', name: 'Child Abuse', renewalMonths: 60 },
      { key: 'criminalRecordCheck', name: 'Criminal', renewalMonths: 60 },
      { key: 'fbiClearance', name: 'FBI', renewalMonths: 60 },
      { key: 'nsorCheck', name: 'NSOR', renewalMonths: 60 },
      { key: 'disclosureStatement', name: 'Disclosure', isOneTime: true, renewalMonths: 0 },
      { key: 'mandatedReporterTraining', name: 'Mandated', renewalMonths: 60 },
      { key: 'fireSafetyTraining', name: 'Fire Safety', renewalMonths: 12 },
      { key: 'emergencyPreparedness', name: 'EPR', renewalMonths: 12 },
      { key: 'healthSafetyBasics', name: 'Health/Safety', isOneTime: true, renewalMonths: 0 },
      { key: 'pediatricFirstAidCPR', name: 'First Aid', renewalMonths: 24 },
      { key: 'profDevTraining', name: 'Prof Dev', renewalMonths: 12 }
    ];

    // Generate summary stats
    const totalStaff = records.length;
    const activeStaff = records.filter(r => r.isActive !== false).length;
    let totalExpiring = 0;
    let totalExpired = 0;

    records.forEach(r => {
      clearanceTypes.forEach(ct => {
        if (ct.isOneTime) return; // Skip one-time items
        const c = r[ct.key];
        if (c?.completionDate) {
          let expDate = c.expirationDate ? parseAnyDate(c.expirationDate) : null;
          if (!expDate && ct.renewalMonths > 0) {
            expDate = calculateExpiration(c.completionDate, ct.renewalMonths);
          }
          if (expDate && !isNaN(expDate.getTime())) {
            expDate.setHours(0, 0, 0, 0);
            const days = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
            if (days < 0) totalExpired++;
            else if (days <= 30) totalExpiring++;
          }
        }
      });
    });

    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Staff Records Summary Report</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;max-width:1200px;margin:0 auto;font-size:11px}
        .logo-header{margin-bottom:8px}.logo-header img{height:50px;width:auto}
        .center-name{text-align:center;font-size:22px;font-weight:bold;color:#1F2937;margin-bottom:15px}
        h1{color:#D97706;border-bottom:2px solid #D97706;padding-bottom:10px;font-size:24px}
        h2{color:#92400E;margin-top:20px;font-size:16px}
        .stats{display:flex;gap:15px;margin-bottom:20px}
        .stat-box{background:#FEF3C7;padding:15px;border-radius:8px;text-align:center;flex:1}
        .stat-value{font-size:24px;font-weight:bold;color:#D97706}
        .stat-label{font-size:11px;color:#92400E}
        table{width:100%;border-collapse:collapse;margin-top:10px;font-size:10px}
        th,td{border:1px solid #D97706;padding:6px;text-align:center}
        th{background:#FEF3C7;color:#92400E;font-weight:bold}
        .status-badge{padding:2px 6px;border-radius:3px;font-weight:bold;font-size:9px}
        .staff-name{text-align:left;font-weight:bold}
        .footer{margin-top:30px;padding-top:15px;border-top:1px solid #D97706;font-size:11px;color:#6B7280;text-align:center}
        @media print{body{padding:0;font-size:9px}table{font-size:8px}}
      </style>
      </head><body>
      <div class="logo-header"><img src="/simple-sign-in-logo.png" alt="Simple Sign In" onerror="this.style.display='none'" /></div>
      <div class="center-name">${organization?.name || 'Childcare Center'}</div>
      <h1>Staff Records Summary Report</h1>
      <p style="color:#6B7280">Generated: ${now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
      
      <div class="stats">
        <div class="stat-box"><div class="stat-value">${totalStaff}</div><div class="stat-label">Total Staff</div></div>
        <div class="stat-box"><div class="stat-value">${activeStaff}</div><div class="stat-label">Active</div></div>
        <div class="stat-box" style="background:#FEF3C7"><div class="stat-value" style="color:#F59E0B">${totalExpiring}</div><div class="stat-label">Expiring Soon</div></div>
        <div class="stat-box" style="background:#FEE2E2"><div class="stat-value" style="color:#EF4444">${totalExpired}</div><div class="stat-label">Expired</div></div>
      </div>

      <h2>Clearances & Certifications Status Matrix</h2>
      <table>
        <thead>
          <tr>
            <th style="text-align:left">Staff Name</th>
            <th>Position</th>
            ${clearanceTypes.map(ct => `<th>${ct.name}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${records.map(r => {
            const posLabel = r.position === 'other' ? r.positionOther : (r.position ? POSITION_OPTIONS.find(p => p.value === r.position)?.label || r.position : '-');
            return `<tr>
              <td class="staff-name">${r.name || 'N/A'}${r.isActive === false ? ' <em>(Inactive)</em>' : ''}</td>
              <td>${posLabel}</td>
              ${clearanceTypes.map(ct => {
                const c = r[ct.key] || r.healthHumanServices;
                const status = getClearanceStatus(c, ct.isOneTime, ct.renewalMonths);
                return `<td style="background:${status.bg}"><span class="status-badge" style="color:${status.color}">${status.status}</span></td>`;
              }).join('')}
            </tr>`;
          }).join('')}
        </tbody>
      </table>

      <h2>🏥 Physical & TB Test Status</h2>
      <table>
        <thead>
          <tr>
            <th style="text-align:left">Staff Name</th>
            <th>Last Physical</th>
            <th>Next Physical Due</th>
            <th>TB Test Date</th>
            <th>TB Result</th>
          </tr>
        </thead>
        <tbody>
          ${records.map(r => `<tr>
            <td class="staff-name">${r.name || 'N/A'}</td>
            <td>${formatDate(r.lastPhysicalDate)}</td>
            <td>${formatDate(r.nextPhysicalDate)}</td>
            <td>${formatDate(r.tbTest?.testDate)}</td>
            <td>${r.tbTest?.result ? r.tbTest.result.charAt(0).toUpperCase() + r.tbTest.result.slice(1) : '-'}</td>
          </tr>`).join('')}
        </tbody>
      </table>

      <div class="footer">
        <p>Staff Records Summary Report</p>
        <p>This report contains confidential employee information. Handle appropriately.</p>
      </div>
      <script>window.onload = function() { window.print(); }</script>
      </body></html>
    `);
    printWindow.document.close();
    toast.success("Summary report generated");
  };

  // Export staff records as Excel backup
  const handleExportBackup = async () => {
    try {
      const response = await axios.get(`${API}/staff-records/export`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `staff_records_backup_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Staff records exported successfully! Save this file as a backup.");
    } catch (error) {
      console.error("Error exporting staff records:", error);
      if (error.response?.status === 404) {
        toast.error("No staff records to export");
      } else {
        toast.error("Failed to export staff records");
      }
    }
  };

  // Download bulk upload template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/staff-records/download-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'staff_records_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Template downloaded! Fill it in and upload to add staff records.");
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Failed to download template");
    }
  };

  // Handle bulk upload
  const handleBulkUpload = async () => {
    if (!uploadFile) {
      toast.error("Please select a file to upload");
      return;
    }
    
    setUploading(true);
    setUploadResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await axios.post(
        `${API}/staff-records/bulk-upload?update_existing=${updateExisting}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setUploadResults(response.data);
      toast.success(response.data.message);
      
      // Refresh the records list
      fetchRecords();
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  // ============== RENDER ==============
  
  return (
    <AppShell pageTitle="Staff Compliance & Training Requirements" breadcrumb>
      {/* Hero Banner */}
      <PageHero 
        imageUrl={HERO_IMAGES.staffTraining}
        title="Staff Compliance & Training"
        subtitle={isOwner ? "Track clearances, certifications & professional development" : "View your clearances and certifications"}
        altText="Staff training session"
      />
      
      <FeatureGate feature="staff_compliance_tracker">
      
      <div data-testid="staff-records-page" className="relative z-10">
        {/* Data Loss Prevention Banner - Show to owners */}
        {isOwner && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-xl" data-testid="data-backup-banner">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-red-800 mb-2">Protect Your Staff Data - Create Regular Backups!</h3>
                <p className="text-sm text-red-700 mb-3">
                  To prevent data loss, we recommend regularly exporting your staff records. You can also use bulk upload to quickly add or restore multiple staff records at once.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    onClick={handleExportBackup}
                    variant="outline"
                    size="sm"
                    className="border-green-500 text-green-600 hover:bg-green-50"
                    data-testid="banner-export-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />Export Current Records
                  </Button>
                  <Button 
                    onClick={handleDownloadTemplate}
                    variant="outline"
                    size="sm"
                    className="border-blue-500 text-blue-600 hover:bg-blue-50"
                    data-testid="banner-template-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />Download Template
                  </Button>
                  <Button 
                    onClick={() => setBulkUploadDialogOpen(true)}
                    variant="outline"
                    size="sm"
                    className="border-blue-500 text-blue-600 hover:bg-blue-50"
                    data-testid="banner-upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />Bulk Upload Records
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {isOwner && (
          <div className="flex flex-wrap gap-2 mb-6">
            <Button 
              onClick={handleExportBackup} 
              variant="outline" 
              className="border-green-500 text-green-600 hover:bg-green-50 font-semibold rounded-xl shadow-md" 
              data-testid="export-backup-btn"
              title="Download Excel backup of all staff records"
            >
              <FileText className="w-5 h-5 mr-2" />Export Backup
            </Button>
            <Button onClick={generateSummaryReport} variant="outline" className="border-blue-500 text-blue-600 hover:bg-blue-50 font-semibold rounded-xl shadow-md" data-testid="summary-report-btn">
              <FileText className="w-5 h-5 mr-2" />Summary Report
            </Button>
            <Button onClick={() => navigate('/settings/policy-acknowledgements')} variant="outline" className="border-purple-500 text-purple-600 hover:bg-purple-50 font-semibold rounded-xl shadow-md" data-testid="policy-acknowledgements-btn">
              <ScrollText className="w-5 h-5 mr-2" />Policy Acknowledgements
            </Button>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg" data-testid="add-staff-btn">
              <Plus className="w-5 h-5 mr-2" />Add Staff Record
            </Button>
          </div>
        )}

        {/* Summary Cards - Show filtered records */}
        <div className="mb-2">
          <h2 className="text-lg font-bold text-blue-800">Staff Compliance Overview</h2>
          <p className="text-xs text-gray-500 mt-0.5">Tracks individual staff clearances and certifications only — not center-wide facility compliance.</p>
        </div>
        <StaffSummaryCards records={filteredRecords} uploadMap={uploadMap} />

        {/* Expiring Clearances Alert - Only show to owner */}
        {isOwner && (
          <ExpiringClearancesAlert
            expiringClearances={expiringClearances}
            alertSettings={alertSettings}
            setAlertSettings={setAlertSettings}
            alertDialogOpen={alertDialogOpen}
            setAlertDialogOpen={setAlertDialogOpen}
            onSendAlert={sendExpirationAlerts}
            onSendToStaff={sendAlertsToStaff}
            sendingAlert={sendingAlert}
          />
        )}

        {/* Staff Records List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-ink-muted">Loading staff records...</p>
          </div>
        ) : filteredRecords.length === 0 ? (
          <Card className="bg-white border border-line shadow-card">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-brand-primary/40 mx-auto mb-4" />
              <p className="text-xl text-ink-main mb-4">
                {isOwner ? "No staff records yet" : "No staff record found for your account"}
              </p>
              {isOwner ? (
                <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-brand-primary hover:bg-brand-primary/90 text-white rounded-xl">
                  <Plus className="w-5 h-5 mr-2" />Add Your First Staff Record
                </Button>
              ) : (
                <Button onClick={() => { 
                  resetForm(); 
                  // Auto-populate email from logged-in user
                  setFormData(prev => ({ 
                    ...prev, 
                    email: currentUser?.email || '',
                    name: currentUser?.name || ''
                  })); 
                  setDialogOpen(true); 
                }} className="bg-brand-primary hover:bg-brand-primary/90 text-white rounded-xl">
                  <Plus className="w-5 h-5 mr-2" />Create My Staff Record
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredRecords.map((record) => {
              return (
              <Card key={record.id} className="bg-white border border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md"><Users className="w-6 h-6 text-white" /></div>
                      <div>
                        <CardTitle className="text-xl font-bold text-blue-900">
                          {record.name}
                          {record.exemptFromEvaluation && (
                            <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs border border-blue-300" data-testid={`exempt-badge-${record.id}`}>
                              Eval Exempt
                            </Badge>
                          )}
                          {uploadIndicators[record.id] > 0 && (
                            <Badge className="ml-2 bg-blue-500 text-white text-xs" data-testid={`upload-indicator-${record.id}`}>
                              {uploadIndicators[record.id]} new upload{uploadIndicators[record.id] > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </CardTitle>
                        <p className="text-sm text-blue-600">{record.email || "No email"}</p>
                        <div className="flex gap-4 mt-1 text-xs text-gray-600">
                          <span className="font-medium">Started: {formatDate(record.startDate)}</span>
                          <span className="font-medium">Tenure: {calculateTenure(record.startDate)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isOwner && (
                        <Button onClick={() => handleEmailReport(record)} variant="outline" size="sm" className="border-blue-400 text-blue-600 hover:bg-blue-50" title="Email Report"><Mail className="w-4 h-4" /></Button>
                      )}
                      <Button onClick={async () => { await exportStaffRecordPDF(record, organization?.name); toast.success("PDF downloaded"); }} variant="outline" size="sm" className="border-purple-400 text-purple-600 hover:bg-purple-50" title="Export to PDF"><FileText className="w-4 h-4" /></Button>
                      <Button onClick={() => handlePrintRecord(record)} variant="outline" size="sm" className="border-emerald-400 text-emerald-600 hover:bg-emerald-50" title="Print"><Printer className="w-4 h-4" /></Button>
                      <Button onClick={() => handleEdit(record)} variant="outline" size="sm" className="border-blue-400 text-blue-600 hover:bg-blue-50" data-testid={`edit-staff-${record.id}`}><Edit className="w-4 h-4" /></Button>
                      {isOwner && (
                        <Button onClick={() => handleDelete(record.id)} variant="outline" size="sm" className="border-red-400 text-red-600 hover:bg-red-50" data-testid={`delete-staff-${record.id}`}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {/* Staff Info Row */}
                  <div className="grid grid-cols-4 gap-4 mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                    <div>
                      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Position</p>
                      <p className="text-sm font-medium text-gray-800">{record.position === 'other' ? record.positionOther : (POSITION_OPTIONS.find(p => p.value === record.position)?.label || record.position || 'Not set')}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Education</p>
                      <p className="text-sm font-medium text-gray-800">{EDUCATION_OPTIONS.find(e => e.value === record.education)?.label || record.education || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Last Physical</p>
                      <p className="text-sm font-medium text-gray-800">{formatDate(record.lastPhysicalDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">TB Test</p>
                      <p className="text-sm font-medium text-gray-800">{record.tbTest?.result ? `${record.tbTest.result.charAt(0).toUpperCase() + record.tbTest.result.slice(1)} (${formatDate(record.tbTest.testDate)})` : 'Not recorded'}</p>
                    </div>
                  </div>

                  {/* Staff Health Form Upload */}
                  {(() => {
                    const healthFormUploads = (clearanceUploads[record.id] || []).filter(u => u.clearanceType === 'staffHealthForm');
                    return (
                      <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid={`health-form-upload-${record.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="text-sm font-bold text-blue-800">Staff Health Form</p>
                            <p className="text-xs text-blue-600">Upload the signed physical exam / health form document</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="cursor-pointer inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg transition" data-testid={`upload-health-form-${record.id}`}>
                              <Upload className="w-4 h-4" />
                              {uploadingClearance === `${record.id}-staffHealthForm` ? 'Uploading...' : 'Upload'}
                              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp"
                                onChange={(e) => { if (e.target.files[0]) handleClearanceUpload(record.id, 'staffHealthForm', e.target.files[0]); e.target.value = ''; }}
                                disabled={uploadingClearance === `${record.id}-staffHealthForm`} />
                            </label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-4 h-4 text-blue-400 hover:text-blue-600 cursor-help" data-testid={`health-form-tooltip-trigger-${record.id}`} />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs text-xs p-3 bg-gray-900 text-white rounded-xl">
                                  <p>Once you've uploaded the document, click the pencil (Edit) icon above to enter the Last Physical Date and TB Test date. Next Physical Due is calculated automatically.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            {isOwner && (
                              <button
                                onClick={() => sendClearanceLink(record.id, 'staffHealthForm', record.name)}
                                disabled={sendingLink === `${record.id}-staffHealthForm`}
                                className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 bg-blue-50 border border-blue-200 px-2 py-1.5 rounded-lg font-medium transition disabled:opacity-50"
                                title="Send upload link to staff"
                                data-testid={`send-link-health-form-${record.id}`}
                              >
                                {sendingLink === `${record.id}-staffHealthForm` ? '...' : '🔗 Send Link'}
                              </button>
                            )}
                          </div>                        </div>
                        {/* Health dates displayed inline */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 p-2 bg-white rounded-lg border border-blue-100">
                          <div>
                            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Last Physical</p>
                            <p className="text-sm font-medium text-gray-800">{record.lastPhysicalDate ? formatDate(record.lastPhysicalDate) : <span className="text-amber-600 italic">Not on file</span>}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Next Physical Due</p>
                            {record.nextPhysicalDate ? (() => {
                              const isOverdue = new Date(record.nextPhysicalDate) < new Date();
                              return (
                                <div>
                                  <p className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-gray-800'}`}>{formatDate(record.nextPhysicalDate)}</p>
                                  {isOverdue && <span className="inline-block mt-0.5 text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Expired</span>}
                                </div>
                              );
                            })() : <p className="text-sm text-amber-600 italic">Not on file</p>}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">TB Test Date</p>
                            <p className="text-sm font-medium text-gray-800">{record.tbTest?.testDate ? formatDate(record.tbTest.testDate) : <span className="text-amber-600 italic">Not on file</span>}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">TB Result</p>
                            <p className={`text-sm font-medium ${record.tbTest?.result === 'positive' ? 'text-red-600 font-semibold' : 'text-gray-800'}`}>
                              {record.tbTest?.result ? record.tbTest.result.charAt(0).toUpperCase() + record.tbTest.result.slice(1) : <span className="text-amber-600 italic">Not on file</span>}
                            </p>
                          </div>
                        </div>
                        {healthFormUploads.length === 0 ? (
                          <p className="text-xs text-blue-500 italic">No health form uploaded yet.</p>
                        ) : (
                          <div className="space-y-2">
                            {healthFormUploads.map(u => (
                              <div key={u.id} className="flex items-center justify-between gap-2 bg-white border border-blue-200 rounded-lg px-3 py-2">
                                <span className="text-sm text-blue-800 font-medium truncate flex-1">{u.originalFilename}</span>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button onClick={() => handleDownloadUpload(u.id, u.originalFilename)}
                                    className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 bg-blue-50 border border-blue-200 px-2 py-1 rounded"
                                    title="Download" data-testid={`download-health-form-${u.id}`}>
                                    <Download className="w-3 h-3" />Download
                                  </button>
                                  <button onClick={() => handlePrintUpload(u.id)}
                                    className="flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded"
                                    title="Print" data-testid={`print-health-form-${u.id}`}>
                                    <Printer className="w-3 h-3" />Print
                                  </button>
                                  <button onClick={() => handleDeleteUpload(u.id, record.id)}
                                    className="text-red-400 hover:text-red-600 p-1 rounded"
                                    title="Remove" data-testid={`delete-health-form-${u.id}`}>
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Clearances Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                          <th className="text-left p-3 font-semibold rounded-tl-lg">Clearance / Certification</th>
                          <th className="text-center p-3 font-semibold">Renewal</th>
                          <th className="text-center p-3 font-semibold">Status</th>
                          <th className="text-center p-3 font-semibold">Completed</th>
                          <th className="text-center p-3 font-semibold">Expires</th>
                          <th className="text-center p-3 font-semibold">Documents</th>
                          {isOwner && <th className="text-center p-3 font-semibold rounded-tr-lg">Send Link</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {CLEARANCE_TYPES.map((type, idx) => {
                          const clearance = record[type.key];
                          const isOdd = idx % 2 === 0;
                          // Always use the correct renewal period from CLEARANCE_TYPES
                          const renewalMonths = type.renewalMonths;
                          
                          // Special row for Prof Dev
                          if (type.key === 'profDevTraining') {
                            const hours = clearance?.currentCycleHours || 0;
                            const coursesCount = clearance?.courses?.length || 0;
                            return (
                              <tr key={type.key} className={isOdd ? 'bg-gray-50' : 'bg-white'}>
                                <td className="p-2 font-medium">{type.label}</td>
                                <td className="p-2 text-center text-gray-500">Annually</td>
                                <td className="p-2 text-center">
                                  <Badge className={hours >= 12 ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}>
                                    {hours} / 12 hrs
                                  </Badge>
                                </td>
                                <td className="p-2 text-center text-gray-500">{coursesCount > 0 ? `${coursesCount} course${coursesCount > 1 ? 's' : ''}` : '-'}</td>
                                <td className="p-2 text-center text-gray-500">Annually</td>
                                <td className="p-2 text-center">
                                  <span className="text-xs text-gray-400 italic" title="Add or update courses and upload each course's certificate in Edit Staff → Professional Development.">
                                    Manage in Edit Staff → Prof Dev
                                  </span>
                                </td>
                                {isOwner && (
                                  <td className="p-2 text-center">
                                    {!['staffEvaluation', 'disclosureStatement', 'emergencyPreparedness', 'shakenBabyPolicy'].includes(type.key) && (
                                      <button
                                        onClick={() => sendClearanceLink(record.id, type.key, record.name)}
                                        disabled={sendingLink === `${record.id}-${type.key}`}
                                        className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 bg-blue-50 border border-blue-200 px-2 py-1 rounded disabled:opacity-50"
                                        title="Send upload link to staff"
                                        data-testid={`send-link-${record.id}-${type.key}`}
                                      >
                                        {sendingLink === `${record.id}-${type.key}` ? '...' : '🔗 Send'}
                                      </button>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          }
                          
                          // Format renewal period for display
                          const formatRenewal = (type, months) => {
                            // Use custom label if available
                            if (type.renewalLabel) {
                              return type.renewalLabel;
                            }
                            if (months >= 12) {
                              const years = months / 12;
                              return `${years} year${years > 1 ? 's' : ''}`;
                            }
                            return `${months} months`;
                          };
                          
                          // Calculate expiration date if not set but completion date exists
                          const getExpirationDate = (clearance, renewalMonths) => {
                            if (!clearance) return null;
                            if (clearance.expirationDate) return clearance.expirationDate;
                            if (clearance.completionDate && renewalMonths > 0) {
                              try {
                                let completionDate;
                                const dateStr = clearance.completionDate;
                                
                                // Handle numeric format (e.g., 8182025 or 8182025.0)
                                if (typeof dateStr === 'number' || /^\d+\.?\d*$/.test(dateStr)) {
                                  let numStr = String(dateStr).split('.')[0];
                                  if (numStr.length === 7) numStr = '0' + numStr;
                                  if (numStr.length === 8) {
                                    const month = parseInt(numStr.substring(0, 2)) - 1;
                                    const day = parseInt(numStr.substring(2, 4));
                                    const year = parseInt(numStr.substring(4, 8));
                                    completionDate = new Date(year, month, day);
                                  }
                                }
                                // Handle MM/DD/YYYY format
                                else if (typeof dateStr === 'string' && dateStr.includes('/')) {
                                  const [month, day, year] = dateStr.split('/');
                                  completionDate = new Date(year, month - 1, day);
                                }
                                // Handle ISO format (YYYY-MM-DD)
                                else {
                                  completionDate = parseISO(dateStr);
                                }
                                
                                if (completionDate && !isNaN(completionDate.getTime())) {
                                  const expiration = addMonths(completionDate, renewalMonths);
                                  return format(expiration, 'yyyy-MM-dd');
                                }
                              } catch (e) {
                                console.error('Error calculating expiration:', e);
                              }
                            }
                            return null;
                          };
                          
                          const calculatedExpiration = type.isOneTime ? null : getExpirationDate(clearance, renewalMonths);

                          // For staffEvaluation: owner's own record = Current, exempt toggle = N/A
                          let evalStatusOverride = null;
                          if (type.key === 'staffEvaluation') {
                            const isOwnRecord = record.email?.toLowerCase() === currentUser?.email?.toLowerCase();
                            if (isOwner && isOwnRecord) {
                              evalStatusOverride = <Badge className="bg-green-100 text-green-700 text-xs border border-green-200">Current</Badge>;
                            } else if (record.exemptFromEvaluation) {
                              evalStatusOverride = <Badge className="bg-blue-100 text-blue-700 text-xs border border-blue-200">N/A</Badge>;
                            }
                          }
                          
                          return (
                            <tr key={type.key} className={isOdd ? 'bg-gray-50' : 'bg-white'}>
                              <td className="p-2 font-medium">{type.label}</td>
                              <td className="p-2 text-center text-gray-500">
                                {type.isOneTime ? 'One-Time' : formatRenewal(type, renewalMonths)}
                              </td>
                              <td className="p-2 text-center">{evalStatusOverride || getStatusBadge(
                                clearance,
                                calculatedExpiration,
                                type.isOneTime,
                                !!(uploadMap[record.id] && uploadMap[record.id][type.key]),
                                () => { setFocusClearanceKey(type.key); handleEdit(record); }
                              )}</td>
                              <td className="p-2 text-center">{formatDate(clearance?.completionDate)}</td>
                              <td className="p-2 text-center">
                                {type.isOneTime
                                  ? (clearance?.completionDate ? 'N/A' : <span className="text-amber-600 font-medium">Not On File</span>)
                                  : (calculatedExpiration ? formatDate(calculatedExpiration) : <span className="text-amber-600 font-medium">Not On File</span>)
                                }
                              </td>
                              <td className="p-2 text-center">
                                {(() => {
                                  const uploads = (clearanceUploads[record.id] || []).filter(u => u.clearanceType === type.key);
                                  return (
                                    <div className="flex items-center justify-center">
                                      <StackedUploads
                                        uploads={uploads}
                                        onDownload={handleDownloadUpload}
                                        onDelete={handleDeleteUpload}
                                        recordId={record.id}
                                        typeKey={type.key}
                                        uploadingKey={uploadingClearance}
                                        onUpload={handleClearanceUpload}
                                      />
                                    </div>
                                  );
                                })()}
                              </td>
                              {isOwner && (
                                <td className="p-2 text-center">
                                  {!['staffEvaluation', 'disclosureStatement', 'emergencyPreparedness', 'shakenBabyPolicy'].includes(type.key) && (
                                    <button
                                      onClick={() => sendClearanceLink(record.id, type.key, record.name)}
                                      disabled={sendingLink === `${record.id}-${type.key}`}
                                      className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 bg-blue-50 border border-blue-200 px-2 py-1 rounded disabled:opacity-50"
                                      title="Send upload link to staff"
                                      data-testid={`send-link-${record.id}-${type.key}`}
                                    >
                                      {sendingLink === `${record.id}-${type.key}` ? '...' : '🔗 Send'}
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl text-blue-800">{editingRecord ? "Edit Staff Record" : "Add Staff Record"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-blue-800">Staff Name *</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                    placeholder="Full name" 
                    required 
                    className="border-blue-200" 
                  />
                </div>
                <div>
                  <Label className="text-blue-800">
                    Email {!isOwner && <span className="text-xs text-gray-500">(Auto from login)</span>}
                  </Label>
                  <Input 
                    type="email" 
                    value={formData.email} 
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                    placeholder="email@example.com" 
                    className={`border-blue-200 ${!isOwner ? 'bg-gray-100' : ''}`}
                    disabled={!isOwner}
                  />
                </div>
                <div>
                  <Label className="text-blue-800">
                    Cell Phone <span className="text-xs text-gray-500">(for SMS)</span>
                  </Label>
                  <Input
                    type="tel"
                    value={formData.cellPhone || ""}
                    onChange={(e) => setFormData({ ...formData, cellPhone: e.target.value })}
                    placeholder="(555) 555-5555"
                    className="border-blue-200"
                    data-testid="staff-cell-phone-input"
                  />
                </div>
                <div>
                  <Label className="text-blue-800">Birth Date</Label>
                  <Input 
                    type="date" 
                    value={formData.birthDate} 
                    onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })} 
                    className="border-blue-200" 
                  />
                </div>
                <div>
                  <Label className="text-blue-800">
                    Start Date * {!isOwner && <span className="text-xs text-gray-500">(Owner only)</span>}
                  </Label>
                  <Input 
                    type="date" 
                    value={formData.startDate} 
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} 
                    required={isOwner}
                    className={`border-blue-200 ${!isOwner ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={!isOwner}
                  />
                  {!isOwner && !formData.startDate && (
                    <p className="text-xs text-blue-600 mt-1">Owner will set your start date</p>
                  )}
                </div>
                <div>
                  <Label className="text-blue-800">Position</Label>
                  <Select value={formData.position} onValueChange={(v) => setFormData({ ...formData, position: v, positionOther: v === 'other' ? formData.positionOther : '' })}>
                    <SelectTrigger className="border-blue-200"><SelectValue placeholder="Select position..." /></SelectTrigger>
                    <SelectContent>
                      {POSITION_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.position === 'other' && (
                    <Input 
                      className="mt-2 border-blue-200" 
                      placeholder="Enter position..."
                      value={formData.positionOther || ''}
                      onChange={(e) => setFormData({ ...formData, positionOther: e.target.value })}
                    />
                  )}
                </div>
                <div>
                  <Label className="text-blue-800">Assigned Category</Label>
                  <Select value={formData.plannerCategory || "__none"} onValueChange={(v) => setFormData({ ...formData, plannerCategory: v === "__none" ? "" : v, plannerRoomId: "" })}>
                    <SelectTrigger className="border-blue-200" data-testid="staff-planner-category-select"><SelectValue placeholder="None / Not Assigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">None / Not Assigned</SelectItem>
                      {PLANNER_CATEGORIES.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-400 mt-1">Used by the Tour &amp; Ratio Planner to count this staff member toward that category's Live and Projected Ratios.</p>
                  {formData.plannerCategory && plannerRooms.filter(r => r.category === formData.plannerCategory).length > 1 && (
                    <div className="mt-2">
                      <Label className="text-blue-800">Assigned Room</Label>
                      <Select value={formData.plannerRoomId || "__none"} onValueChange={(v) => setFormData({ ...formData, plannerRoomId: v === "__none" ? "" : v })}>
                        <SelectTrigger className="border-blue-200" data-testid="staff-planner-room-select"><SelectValue placeholder="Choose a room" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">Choose a room</SelectItem>
                          {plannerRooms.filter(r => r.category === formData.plannerCategory).map(r => (
                            <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400 mt-1">This category has more than one room — pick which physical room this staff member works in.</p>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-blue-800">Second Assigned Category (optional)</Label>
                  <Select value={formData.plannerCategory2 || "__none"} onValueChange={(v) => setFormData({ ...formData, plannerCategory2: v === "__none" ? "" : v, plannerRoomId2: "", plannerCoverageType2: v === "__none" ? "different_shift" : formData.plannerCoverageType2 })}>
                    <SelectTrigger className="border-blue-200" data-testid="staff-planner-category2-select"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">None</SelectItem>
                      {PLANNER_CATEGORIES.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-400 mt-1">If this staff member also helps in a second category, they'll show as "Shared Staff" on that category's Live Ratios card.</p>
                  {formData.plannerCategory2 && plannerRooms.filter(r => r.category === formData.plannerCategory2).length > 1 && (
                    <div className="mt-2">
                      <Label className="text-blue-800">Second Assigned Room</Label>
                      <Select value={formData.plannerRoomId2 || "__none"} onValueChange={(v) => setFormData({ ...formData, plannerRoomId2: v === "__none" ? "" : v })}>
                        <SelectTrigger className="border-blue-200" data-testid="staff-planner-room2-select"><SelectValue placeholder="Choose a room" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">Choose a room</SelectItem>
                          {plannerRooms.filter(r => r.category === formData.plannerCategory2).map(r => (
                            <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400 mt-1">This category has more than one room — pick which physical room this staff member is shared into.</p>
                    </div>
                  )}
                  {formData.plannerCategory2 && (
                    <div className="mt-2">
                      <Label className="text-blue-800">Coverage Type</Label>
                      <Select value={formData.plannerCoverageType2 || "different_shift"} onValueChange={(v) => setFormData({ ...formData, plannerCoverageType2: v })}>
                        <SelectTrigger className="border-blue-200" data-testid="staff-planner-coverage-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="different_shift">Different Shift</SelectItem>
                          <SelectItem value="same_time">Same Time</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400 mt-1">
                        Choose "Same Time" only if this staff member is supervising both groups together — the combined group then follows the
                        youngest/strictest ratio present, and their ratio allowance is shared across both rooms instead of counted fully in each.
                        Choose "Different Shift / Time" if this is simply a second assignment at a different time of day — each room keeps its own ratio, unchanged.
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-blue-800">Status {!isOwner && <span className="text-xs text-gray-500">(Owner only)</span>}</Label>
                  <Select 
                    value={formData.isActive ? "active" : "inactive"} 
                    onValueChange={(v) => setFormData({ ...formData, isActive: v === "active" })}
                    disabled={!isOwner}
                  >
                    <SelectTrigger className={`border-blue-200 ${!isOwner ? 'bg-gray-100' : ''}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                  </Select>
                </div>
                {isOwner && (
                  <div className="flex items-center gap-3 col-span-2">
                    <Switch
                      id="exemptFromEvaluation"
                      checked={formData.exemptFromEvaluation}
                      onCheckedChange={(v) => setFormData({ ...formData, exemptFromEvaluation: v })}
                      data-testid="exempt-from-evaluation-toggle"
                    />
                    <Label htmlFor="exemptFromEvaluation" className="text-blue-800 cursor-pointer">
                      Exempt from Annual Evaluation
                      <span className="block text-xs text-gray-500 font-normal">Staff member will not be counted in evaluation compliance (e.g., Director/Owner)</span>
                    </Label>
                  </div>
                )}
              </div>
              
              {/* Education */}
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-blue-800">Education</Label>
                  <Select value={formData.education} onValueChange={(v) => setFormData({ ...formData, education: v, educationOther: v !== "other" ? "" : formData.educationOther })}>
                    <SelectTrigger className="border-blue-200"><SelectValue placeholder="Select education level..." /></SelectTrigger>
                    <SelectContent>
                      {EDUCATION_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.education === "other" && (
                  <div><Label className="text-blue-800">Other Education (specify)</Label>
                    <Input value={formData.educationOther} onChange={(e) => setFormData({ ...formData, educationOther: e.target.value })} placeholder="Please specify..." className="border-blue-200" />
                  </div>
                )}
              </div>

              {/* Physical Exam & TB Test */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">Physical Exam & TB Test</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-blue-800">Last Physical Date</Label>
                    <Input 
                      type="date" 
                      value={formData.lastPhysicalDate} 
                      onChange={(e) => handlePhysicalDateChange(e.target.value)} 
                      className="border-blue-200" 
                    />
                    {formData.lastPhysicalDate && (
                      <p className={`text-xs mt-1 ${getPhysicalStatus() === 'expired' ? 'text-red-600' : getPhysicalStatus() === 'completed' ? 'text-green-600' : 'text-blue-600'}`}>
                        Status: {getPhysicalStatus().charAt(0).toUpperCase() + getPhysicalStatus().slice(1)}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-blue-800">Next Physical Due (Auto: 2 yrs)</Label>
                    <Input 
                      type="date" 
                      value={formData.nextPhysicalDate} 
                      onChange={(e) => setFormData({ ...formData, nextPhysicalDate: e.target.value })} 
                      className="border-blue-200" 
                    />
                    {formData.nextPhysicalDate && (
                      <p className="text-xs mt-1 text-gray-500">
                        Auto-generated from last physical
                      </p>
                    )}
                  </div>
                  <div><Label className="text-blue-800">TB Test (Mantoux) Date</Label><Input type="date" value={formData.tbTest?.testDate || ""} onChange={(e) => setFormData({ ...formData, tbTest: { ...formData.tbTest, testDate: e.target.value } })} className="border-blue-200" /></div>
                  <div><Label className="text-blue-800">TB Test Result</Label>
                    <Select value={formData.tbTest?.result || ""} onValueChange={(v) => setFormData({ ...formData, tbTest: { ...formData.tbTest, result: v } })}>
                      <SelectTrigger className="border-blue-200"><SelectValue placeholder="Select result" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="negative">Negative</SelectItem>
                        <SelectItem value="positive">Positive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {formData.tbTest?.result === "positive" && (
                  <div className="mt-3">
                    <Label className="text-blue-800">TB Positive - Explanation/Follow-up</Label>
                    <Input 
                      value={formData.tbTest?.explanation || ""} 
                      onChange={(e) => setFormData({ ...formData, tbTest: { ...formData.tbTest, explanation: e.target.value } })} 
                      placeholder="Enter chest X-ray results, treatment plan, or other follow-up details..."
                      className="border-blue-200"
                    />
                  </div>
                )}
              </div>

              {/* Clearances */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">Clearances & Certifications</h3>
                <Tabs defaultValue="childAbuseClearance" className="w-full">
                  <TabsList className="grid grid-cols-6 gap-1 mb-2 bg-blue-100 p-1 h-auto">
                    {CLEARANCE_TYPES.slice(0, 6).map((type) => (
                      <TabsTrigger key={type.key} value={type.key} className="text-xs font-semibold py-2 px-1 data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md">
                        {type.shortLabel}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <TabsList className="grid grid-cols-5 gap-1 mb-4 bg-blue-100 p-1 h-auto">
                    {CLEARANCE_TYPES.slice(6).map((type) => (
                      <TabsTrigger key={type.key} value={type.key} className="text-xs font-semibold py-2 px-1 data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md">
                        {type.shortLabel}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {CLEARANCE_TYPES.map((type) => (
                    <TabsContent key={type.key} value={type.key} data-clearance-key={type.key}>
                      <Card className="border-blue-200">
                        <CardHeader className="pb-2"><CardTitle className="text-lg text-blue-800 flex items-center gap-2"><Shield className="w-5 h-5" />{type.label}</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                          {type.key !== "profDevTraining" && (
                          <div className={`grid ${type.isOneTime ? 'grid-cols-2' : 'grid-cols-4'} gap-4`}>
                            <div>
                              <Label className="text-sm text-blue-700">Status (Auto-calculated)</Label>
                              <div className={`mt-1 px-3 py-2 rounded-md text-sm font-medium ${
                                calculateStatus(formData[type.key]?.completionDate, formData[type.key]?.expirationDate, type.isOneTime) === 'expired' 
                                  ? 'bg-red-100 text-red-800' 
                                  : calculateStatus(formData[type.key]?.completionDate, formData[type.key]?.expirationDate, type.isOneTime) === 'completed' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-blue-100 text-blue-800'
                              }`}>
                                {calculateStatus(formData[type.key]?.completionDate, formData[type.key]?.expirationDate, type.isOneTime).charAt(0).toUpperCase() + 
                                 calculateStatus(formData[type.key]?.completionDate, formData[type.key]?.expirationDate, type.isOneTime).slice(1)}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {!formData[type.key]?.completionDate ? "Enter date to update status" : 
                                 type.isOneTime ? "One-time requirement" : "Based on expiration date"}
                              </p>
                            </div>
                            <div>
                              <Label className="text-sm text-blue-700">{type.key === "disclosureStatement" ? "Signed Date" : "Completion Date"}</Label>
                              <Input type="date" value={formData[type.key]?.completionDate || ""} onChange={(e) => updateClearance(type.key, "completionDate", e.target.value)} className="border-blue-200" />
                            </div>
                            {!type.isOneTime && (
                              <>
                                <div>
                                  <Label className="text-sm text-blue-700">Renewal Period (months)</Label>
                                  <Input 
                                    type="number" 
                                    min="1" 
                                    value={formData[type.key]?.renewalPeriodMonths || type.renewalMonths} 
                                    onChange={(e) => updateClearance(type.key, "renewalPeriodMonths", parseInt(e.target.value) || type.renewalMonths)} 
                                    className="border-blue-200" 
                                  />
                                  <p className="text-xs text-gray-500 mt-1">Default: {type.renewalMonths} months</p>
                                </div>
                                <div>
                                  <Label className="text-sm text-blue-700">Expiration Date (Auto-calculated)</Label>
                                  <Input type="date" value={formData[type.key]?.expirationDate || ""} onChange={(e) => updateClearance(type.key, "expirationDate", e.target.value)} className="border-blue-200" />
                                  {formData[type.key]?.completionDate && formData[type.key]?.expirationDate && (
                                    <p className="text-xs text-green-600 mt-1">Auto-generated: {formData[type.key]?.renewalPeriodMonths || type.renewalMonths} months from completion</p>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                          )} {/* end: type.key !== "profDevTraining" grid */}
                          
                          {/* Fire Safety Training - Show credit hours info */}
                          {type.key === "fireSafetyTraining" && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                              <p className="text-sm text-green-700">
                                <strong>Auto-sync:</strong> 1 credit hour per year. Saving with a completion date automatically adds <strong>"Fire Safety Training"</strong> to your Prof Dev 12hr course list and counts toward the annual requirement.
                              </p>
                            </div>
                          )}

                          {/* CPR / First Aid - Credit hours input + upload + auto-sync info */}
                          {type.key === "pediatricFirstAidCPR" && (
                            <div className="space-y-3 mt-2">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-sm text-blue-700">Credit Hours Completed</Label>
                                  <Input
                                    type="number"
                                    step="0.5"
                                    min="0.5"
                                    max="24"
                                    value={formData.pediatricFirstAidCPR?.creditHours || ""}
                                    onChange={(e) => updateClearance("pediatricFirstAidCPR", "creditHours", e.target.value)}
                                    placeholder="e.g. 4"
                                    className="border-blue-200 mt-1"
                                    data-testid="cpr-credit-hours-input"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">Hours vary by institution</p>
                                </div>
                              </div>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <p className="text-sm text-green-700">
                                  <strong>Auto-sync:</strong> When saved with a completion date and credit hours, this certification automatically adds to your <strong>Prof Dev 12hr</strong> course list. Credit hours count only for the compliance year the training was taken — they do not repeat in year 2 of the 2-year renewal period.
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {/* Health & Safety Basics - 6 credit hours, one-time */}
                          {type.key === "healthSafetyBasics" && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                              <p className="text-sm text-green-700">
                                <strong>Auto-sync:</strong> Completing Health &amp; Safety Training counts as <strong>6 credit hours</strong> of professional development — automatically added to the Prof Dev 12hr log when saved. This is a one-time requirement. The 6 credit hours apply only to the compliance year this training was completed and do not carry forward to future years.
                              </p>
                            </div>
                          )}

                          {/* Shaken Baby Policy - one-time, upload signed acknowledgement */}
                          {type.key === "shakenBabyPolicy" && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                              <p className="text-sm text-blue-700">
                                <strong>One-time requirement:</strong> Staff must read and sign the Shaken Baby / Abuse / Head Trauma policy. After receiving and signing the policy acknowledgement link sent by your director, upload your signed copy here.
                              </p>
                            </div>
                          )}

                          {/* Mandated Reporter - 3 credit hours per renewal */}
                          {type.key === "mandatedReporterTraining" && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                              <p className="text-sm text-green-700">
                                <strong>Auto-sync:</strong> Each Mandated Reporter renewal counts as <strong>3 credit hours</strong> — automatically added to the Prof Dev 12hr log when saved with a completion date.
                              </p>
                            </div>
                          )}

                          {/* Emergency Preparedness (EPR) Review */}
                          {type.key === "emergencyPreparedness" && (
                            <div className="border-t pt-4 mt-4">
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                                  <ClipboardList className="w-4 h-4" /> Emergency Preparedness Review (EPR) Requirements
                                </h4>
                                <p className="text-sm text-blue-700 mb-3">
                                  <strong>Annual Review:</strong> The Emergency Preparedness & Response (EPR) Plan must be reviewed with staff during orientation and annually thereafter.
                                </p>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label className="text-sm text-blue-700">Initial Orientation Date</Label>
                                    <Input 
                                      type="date" 
                                      value={formData.emergencyPreparedness?.orientationDate || ""} 
                                      onChange={(e) => setFormData({
                                        ...formData, 
                                        emergencyPreparedness: { 
                                          ...formData.emergencyPreparedness, 
                                          orientationDate: e.target.value 
                                        }
                                      })} 
                                      className="border-blue-200 bg-white" 
                                    />
                                    <p className="text-xs text-blue-600 mt-1">Date staff first received EPR orientation</p>
                                  </div>
                                  <div>
                                    <Label className="text-sm text-blue-700">Last Annual Review Date</Label>
                                    <Input 
                                      type="date" 
                                      value={formData.emergencyPreparedness?.completionDate || ""} 
                                      onChange={(e) => updateClearance("emergencyPreparedness", "completionDate", e.target.value)} 
                                      className="border-blue-200 bg-white" 
                                    />
                                    <p className="text-xs text-blue-600 mt-1">Most recent annual EPR review</p>
                                  </div>
                                </div>
                                <div className="mt-3 p-2 bg-blue-100 rounded text-xs text-blue-800">
                                  <strong>EPR Plan Topics:</strong> Emergency contacts, evacuation routes, shelter-in-place procedures, reunification plans, medical emergencies, weather emergencies, and communication protocols.
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* 12-Hour Professional Development Training Tab */}
                          {type.key === "profDevTraining" && (
                            <div className="space-y-3">
                              {/* Status banner — auto-calculated from course hours */}
                              <div className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
                                (formData.profDevTraining?.currentCycleHours || 0) >= 12
                                  ? 'bg-green-50 border-green-300'
                                  : 'bg-blue-50 border-blue-200'
                              }`}>
                                <div>
                                  <p className={`text-sm font-semibold ${(formData.profDevTraining?.currentCycleHours || 0) >= 12 ? 'text-green-800' : 'text-blue-800'}`}>
                                    {(formData.profDevTraining?.currentCycleHours || 0) >= 12 ? 'Requirement Met' : 'In Progress'}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5">12 clock hours required annually — auto-calculated from courses below</p>
                                </div>
                                <Badge className={`text-sm px-3 py-1 ${(formData.profDevTraining?.currentCycleHours || 0) >= 12 ? 'bg-green-600 text-white' : 'bg-blue-100 text-blue-800'}`}>
                                  {formData.profDevTraining?.currentCycleHours || 0} / 12 hrs
                                </Badge>
                              </div>
                              <p className="text-xs text-gray-500 italic -mt-1">
                                A read-only, synced summary of this record is visible in the Professional Development Hub — under "My Training Log" for this staff member, or "Staff Training Progress" for owners/admins.
                              </p>
                              <div>
                                <h4 className="font-semibold text-blue-800 mb-2">Professional Development Courses</h4>
                              <div className="space-y-2">
                                {(formData.profDevTraining?.courses || []).map((course, idx) => {
                                  if (course.archived) return null;
                                  return (
                                  <div key={course.id || idx} data-testid={`prof-dev-course-row-${course.id || idx}`} className={`flex items-center gap-2 p-2 rounded ${course.autoSource ? 'bg-green-50 border border-green-200' : 'bg-blue-50'}`}>
                                    {course.autoSource ? (
                                      <div className="flex-1 grid grid-cols-3 gap-2 items-center">
                                        <div className="flex items-center gap-1">
                                          <span className="text-sm text-green-800 font-medium truncate">{course.courseName}</span>
                                          <span className="text-xs bg-green-600 text-white px-1.5 py-0.5 rounded-full ml-1 whitespace-nowrap">Auto-logged</span>
                                        </div>
                                        <span className="text-sm text-green-700 text-center">{course.credits} hrs</span>
                                        <span className="text-sm text-green-700 text-center">{course.dateCompleted}</span>
                                      </div>
                                    ) : (
                                      <div className="flex-1 grid grid-cols-3 gap-2">
                                        <Input value={course.courseName} onChange={(e) => {const newCourses = [...(formData.profDevTraining?.courses || [])];newCourses[idx].courseName = e.target.value;setFormData({...formData,profDevTraining: { ...formData.profDevTraining, courses: newCourses }});}} placeholder="Course Name" className="border-blue-200 text-sm h-8" />
                                        <Input type="number" min="0" step="0.5" value={course.credits} onChange={(e) => {const newCourses = [...(formData.profDevTraining?.courses || [])];newCourses[idx].credits = parseFloat(e.target.value) || 0;setFormData({...formData,profDevTraining: { ...formData.profDevTraining, courses: newCourses }});}} placeholder="Hours" className="border-blue-200 text-sm h-8" />
                                        <Input type="date" value={course.dateCompleted} onChange={(e) => {const newCourses = [...(formData.profDevTraining?.courses || [])];newCourses[idx].dateCompleted = e.target.value;setFormData({...formData,profDevTraining: { ...formData.profDevTraining, courses: newCourses }});}} required title="Completion date is required" className={`text-sm h-8 ${!course.dateCompleted ? 'border-red-300 bg-red-50' : 'border-blue-200'}`} />
                                      </div>
                                    )}
                                    {course.id ? (
                                      <>
                                        {!course.autoSource && (
                                          <CourseCertUpload
                                            recordId={editingRecord.id}
                                            courseId={course.id}
                                            uploads={(clearanceUploads[editingRecord.id] || []).filter(u => u.clearanceType === 'profDevTraining' && u.courseId === course.id)}
                                            isUploading={uploadingClearance === `${editingRecord.id}-profDevTraining-${course.id}`}
                                            onUpload={(file) => handleClearanceUpload(editingRecord.id, 'profDevTraining', file, course.id)}
                                            onDownload={handleDownloadUpload}
                                            onDelete={handleDeleteUpload}
                                          />
                                        )}
                                        <Button type="button" variant="ghost" size="sm" title="Archive — keeps this course in history but removes it from the active list and compliance total" onClick={() => archiveProfDevCourse(course.id)} data-testid={`prof-dev-archive-btn-${course.id}`} className="text-amber-600 hover:bg-amber-50 h-8 w-8 p-0"><Archive className="w-4 h-4" /></Button>
                                        <Button type="button" variant="ghost" size="sm" title="Delete permanently — cannot be undone" onClick={() => deleteProfDevCourse(course.id)} data-testid={`prof-dev-delete-btn-${course.id}`} className="text-red-500 hover:bg-red-50 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
                                      </>
                                    ) : (
                                      !course.autoSource && (
                                        <Button type="button" variant="ghost" size="sm" onClick={() => {const newCourses = (formData.profDevTraining?.courses || []).filter((_, i) => i !== idx);setFormData({...formData,profDevTraining: { ...formData.profDevTraining, courses: newCourses }});}} className="text-red-500 hover:bg-red-50 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
                                      )
                                    )}
                                  </div>
                                  );
                                })}
                                <Button type="button" variant="outline" size="sm" onClick={() => {const newCourses = [...(formData.profDevTraining?.courses || []), { courseName: "", credits: 0, dateCompleted: "" }];setFormData({...formData,profDevTraining: { ...formData.profDevTraining, courses: newCourses }});}} className="w-full border-dashed border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="prof-dev-add-course-btn">
                                  <Plus className="w-4 h-4 mr-2" />Add Course / Training
                                </Button>
                                {(formData.profDevTraining?.courses || []).some(c => c.archived) && (
                                  <div className="mt-3 border border-gray-200 rounded-lg" data-testid="prof-dev-archived-section">
                                    <button type="button" onClick={() => setShowArchivedProfDev(!showArchivedProfDev)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50 rounded-lg" data-testid="prof-dev-archived-toggle">
                                      <span>Archived Training ({(formData.profDevTraining?.courses || []).filter(c => c.archived).length})</span>
                                      {showArchivedProfDev ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                    </button>
                                    {showArchivedProfDev && (
                                      <div className="space-y-1.5 px-3 pb-3">
                                        {(formData.profDevTraining?.courses || []).filter(c => c.archived).map((course) => (
                                          <div key={course.id} data-testid={`prof-dev-archived-row-${course.id}`} className="flex items-center gap-2 p-2 rounded bg-gray-50 border border-gray-200">
                                            <div className="flex-1 grid grid-cols-3 gap-2 items-center">
                                              <span className="text-sm text-gray-600 truncate">{course.courseName}</span>
                                              <span className="text-sm text-gray-500 text-center">{course.credits} hrs</span>
                                              <span className="text-sm text-gray-500 text-center">{course.dateCompleted}</span>
                                            </div>
                                            <Button type="button" variant="outline" size="sm" onClick={() => restoreProfDevCourse(course.id)} data-testid={`prof-dev-restore-btn-${course.id}`} className="text-blue-700 border-blue-300 hover:bg-blue-50 h-8 px-2 text-xs">
                                              <RotateCcw className="w-3.5 h-3.5 mr-1" />Restore
                                            </Button>
                                            <Button type="button" variant="ghost" size="sm" title="Delete permanently — cannot be undone" onClick={() => deleteProfDevCourse(course.id)} data-testid={`prof-dev-delete-btn-${course.id}`} className="text-red-500 hover:bg-red-50 h-8 w-8 p-0">
                                              <Trash2 className="w-4 h-4" />
                                            </Button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2" data-testid="prof-dev-cert-reminder">
                                  Remember to upload the supporting certificate/document in Edit Staff for each applicable Professional Development course. The certificate is supporting documentation only — the manually entered credit hours above are what determine the 12-hour compliance calculation.
                                </p>
                              </div>
                              </div> {/* end courses wrapper */}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>

              {/* Notes */}
              <div className="border-t pt-4"><Label className="text-brand-primary">Notes</Label><Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes..." className="border-line" /></div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button type="submit" className="flex-1 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-xl">{editingRecord ? "Update Record" : "Create Record"}</Button>
                <Button type="button" onClick={() => { setDialogOpen(false); resetForm(); }} variant="outline" className="flex-1 border-line text-ink-main rounded-xl">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={bulkUploadDialogOpen} onOpenChange={setBulkUploadDialogOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-xl text-blue-800 flex items-center gap-2">
                <Upload className="w-6 h-6" />
                Bulk Upload Staff Records
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">How to use:</h4>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Download the Excel template (if you haven't already)</li>
                  <li>Fill in your staff data following the template format</li>
                  <li>Remove the example row before uploading</li>
                  <li>Upload the completed file below</li>
                </ol>
              </div>

              {/* Download Template Button */}
              <Button 
                onClick={handleDownloadTemplate}
                variant="outline"
                className="w-full border-blue-500 text-blue-600 hover:bg-blue-50"
                data-testid="dialog-download-template-btn"
              >
                <Download className="w-4 h-4 mr-2" />Download Excel Template
              </Button>

              {/* File Input */}
              <div>
                <Label className="text-blue-800 mb-2 block">Select Excel File (.xlsx)</Label>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="border-blue-200"
                  data-testid="file-input"
                />
                {uploadFile && (
                  <p className="text-sm text-green-600 mt-1">Selected: {uploadFile.name}</p>
                )}
              </div>

              {/* Update Existing Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="updateExisting"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                  className="rounded border-blue-300"
                  data-testid="update-existing-checkbox"
                />
                <Label htmlFor="updateExisting" className="text-sm text-gray-700 cursor-pointer">
                  Update existing records if name matches (otherwise skip duplicates)
                </Label>
              </div>

              {/* Upload Results */}
              {uploadResults && (
                <div className={`p-4 rounded-lg ${uploadResults.error_count > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                  <h4 className={`font-semibold mb-2 ${uploadResults.error_count > 0 ? 'text-yellow-800' : 'text-green-800'}`}>
                    Upload Results
                  </h4>
                  <div className="grid grid-cols-4 gap-2 text-sm mb-3">
                    <div className="text-center p-2 bg-green-100 rounded">
                      <p className="font-bold text-green-700">{uploadResults.created_count}</p>
                      <p className="text-green-600 text-xs">Created</p>
                    </div>
                    <div className="text-center p-2 bg-blue-100 rounded">
                      <p className="font-bold text-blue-700">{uploadResults.updated_count}</p>
                      <p className="text-blue-600 text-xs">Updated</p>
                    </div>
                    <div className="text-center p-2 bg-gray-100 rounded">
                      <p className="font-bold text-gray-700">{uploadResults.skipped_count}</p>
                      <p className="text-gray-600 text-xs">Skipped</p>
                    </div>
                    <div className="text-center p-2 bg-red-100 rounded">
                      <p className="font-bold text-red-700">{uploadResults.error_count}</p>
                      <p className="text-red-600 text-xs">Errors</p>
                    </div>
                  </div>
                  {uploadResults.details && uploadResults.details.length > 0 && (
                    <div className="max-h-40 overflow-y-auto text-xs text-gray-600 bg-white p-2 rounded border">
                      {uploadResults.details.map((detail, idx) => (
                        <p key={idx} className={detail.includes('CREATED') ? 'text-green-600' : detail.includes('UPDATED') ? 'text-blue-600' : detail.includes('Error') ? 'text-red-600' : 'text-gray-600'}>
                          {detail}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  onClick={handleBulkUpload}
                  disabled={!uploadFile || uploading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                  data-testid="upload-btn"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />Upload Records
                    </>
                  )}
                </Button>
                <Button 
                  type="button" 
                  onClick={() => { 
                    setBulkUploadDialogOpen(false); 
                    setUploadFile(null); 
                    setUploadResults(null); 
                  }} 
                  variant="outline" 
                  className="flex-1 border-gray-300 text-gray-600 rounded-xl"
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Email Report Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-700">
                <Mail className="w-5 h-5" />
                Email Staff Record Report
              </DialogTitle>
            </DialogHeader>
            {emailRecord && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="font-semibold text-blue-900">{emailRecord.name}</p>
                  <p className="text-sm text-blue-700">Staff record report with all clearances, certifications, and health records</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Send To</label>
                  <input
                    type="email"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="staff@email.com"
                    data-testid="email-report-to"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note to Staff (optional)</label>
                  <textarea
                    value={emailNote}
                    onChange={(e) => setEmailNote(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="e.g., Please review your clearances — your First Aid certification expires next month."
                    data-testid="email-report-note"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
                  <Button
                    onClick={sendEmailReport}
                    disabled={!emailTo}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="send-email-report-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Report
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

      </div>
      </FeatureGate>
    </AppShell>
  );
};

export default StaffRecordsPage;
